// ─────────────────────────────────────────────────────────────────────────────
// PsyPractice — Yasal Dokümanlar / Legal Documents (TR + EN + RU)
//
// Bu modül, footer'dan erişilen /legal sayfasının tüm içeriğini barındırır:
// Kullanım Koşulları & Sorumluluk Reddi, Gizlilik Politikası, KVKK Aydınlatma
// Metni, Açık Rıza Metni ve Çerez Politikası. Her belge üç dilde (tr/en/ru) tanımlıdır;
// sayfa aktif locale'e göre doğru dili seçer (useLocale()).
//
// Metinler TEMEL STANDART olarak AB GDPR (2016/679) esas alınarak; Türkiye'deki
// kullanıcılar için KVKK (özellikle yurt dışı aktarım — md. 9) eklenerek; ayrıca
// barındırmanın AB'de (Hollanda / Oracle Cloud) olduğu ve AI işlemenin Anthropic'e
// (gerektiğinde SCC ile AB/AEA dışına) aktarıldığı gerçeği yansıtılarak; sorumluluk
// tamamen Kullanıcıya ait olacak ve klinik-hukuki sorumluluk reddi vurgulu olacak
// şekilde hazırlanmıştır.
//
// ⚠️ SÖZCÜK KURALI: Bu dosyadaki hiçbir kullanıcıya dönük metin ürünü bir "eğitim",
// "kurs", "sertifika" ("training", "course", "certificate") hizmeti gibi tanımlamaz.
// Gerekçe vergiseldir: bu vokabüler ürünü eğitim hizmeti saydırıp GVK 89/1-13 kazanç
// indirimini bakanlık izni şartına bağlama riski doğurur. Ürün bir YAZILIM aracıdır;
// tek istisna, Mesafeli Satış ve Kullanım Koşullarındaki açık OLUMSUZLAMA cümlesidir.
//
// ⚠️ TASLAK: Yürürlüğe alınmadan önce bir avukat / KVKK uyum danışmanı gözden geçirmeli.
// ─────────────────────────────────────────────────────────────────────────────

import type { Locale } from "@/i18n/config";

// Belge sürümü. Kullanıcının kabul ettiği sürüm bundan eskiyse onay ekranı yeniden
// çıkar (bkz. lib/postAuth.ts + backend/legal.py). Metinlerde ESASA ilişkin bir
// değişiklik yapıldığında bu sabit ve belgelerin `updated` tarihleri birlikte artar.
// ⚠️ Backend'deki backend/legal.py → LEGAL_VERSION ile AYNI değeri taşımalıdır.
export const LEGAL_VERSION = "2026-08-01";

export interface LegalSection {
  heading?: string;
  paras?: string[];
  bullets?: string[];
}

export interface LegalDoc {
  id: string; // footer & deep-link anchor (örn. /legal#terms)
  title: string;
  description: string;
  updated: string;
  sections: LegalSection[];
}

const OWNER_TR = 'PsyPractice (bundan böyle "Platform" veya "Veri Sorumlusu")';
const OWNER_EN = 'PsyPractice (hereinafter the "Platform" or "Data Controller")';
const OWNER_RU = 'PsyPractice (далее — «Платформа» или «Оператор данных»)';

// ── Satıcı / Merchant kimliği ─────────────────────────────────────────────────
// iyzico başvurusu, Mesafeli Satış Sözleşmesi ve Hakkımızda sayfası için yasal
// olarak zorunlu satıcı bilgileri. TEK DOĞRU KAYNAK burasıdır — Hakkımızda,
// Mesafeli Satış ve Teslimat/İade belgelerinin tamamı bu sabitten okur; resmî
// şahıs işletmesi bilgileri netleşince yalnızca aşağıdaki alanlar güncellenir.
export const MERCHANT = {
  brand: "PsyPractice",
  legalName: "Mertcan Aykul",
  legalNameEn: "Mertcan Aykul (sole proprietorship)",
  type: "Şahıs İşletmesi",
  typeEn: "Sole proprietorship",
  typeRu: "Индивидуальный предприниматель",
  // Vergi dairesine bildirilmiş MERKEZ ADRESİ. E-ticaret yönetmeliği m.5 bunu
  // ana sayfada "iletişim" başlığı altında doğrudan ulaşılabilir şekilde ister;
  // Mesafeli Sözleşmeler Yönetmeliği m.5 de ön bilgilendirmede açık adres arar.
  // ⚠️ ÖNCELİKLİ: bu şu an konut adresidir. Fethiye/Muğla içinde bir sanal ofise
  // geçilip kayıtlı adres taşındığında burası ve backend/merchant.py birlikte
  // güncellenecek (il dışına çıkmamak önemli: özelgeyi Muğla Defterdarlığı verdi).
  address: "Akarca Mah. 894 (AKR) Sk. No: 16/2, Fethiye / Muğla, Türkiye",
  addressEn: "Akarca Mah. 894 (AKR) Sk. No: 16/2, Fethiye / Muğla, Türkiye",
  taxOffice: "Fethiye Vergi Dairesi",
  taxNo: "1220413958",
  email: "info@psycora.com",
  // ⚠️ ÖNCELİKLİ: bu kişisel numaradır, geçici çözümdür. Bulut santral numarası
  // alınınca burası, backend/merchant.py ve landing/index.html birlikte güncellenir.
  phone: "+90 542 328 70 12",
  website: "https://psypractice.psycora.com",
};

// ── Türkçe ───────────────────────────────────────────────────────────────────
const DOCS_TR: LegalDoc[] = [
  {
    id: "terms",
    title: "Kullanım Koşulları & Sorumluluk Reddi",
    description:
      "Hizmetin niteliği ve sınırları, neyin yerine geçmediği, kullanıcı taahhütleri ve tüm sorumluluğun kullanıcıya ait olduğu kapsamlı feragat.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "1. Taraflar ve Kabul",
        paras: [
          `Bu Kullanım Koşulları, ${OWNER_TR} ile Platforma erişen veya onu kullanan gerçek/tüzel kişi (bundan böyle "Kullanıcı") arasında akdedilir. Kullanıcı, hesap oluşturarak, oturum açarak veya Platformu herhangi bir biçimde kullanarak bu koşulların tamamını okuduğunu, anladığını ve kayıtsız şartsız kabul, beyan ve taahhüt ettiğini onaylar. Koşulları kabul etmeyen kişi Platformu kullanmamalıdır.`,
        ],
      },
      {
        heading: "2. Hizmetin Niteliği ve Sınırları",
        bullets: [
          "PsyPractice, psikoloji öğrencilerinin ve klinisyen adaylarının görüşme pratiğini güvenli bir ortamda yürütmesini ve kaydetmesini sağlayan, yapay zekâ destekli bir hasta SİMÜLASYON yazılımıdır.",
          "PsyPractice bir eğitim veya öğretim hizmeti değil, kullanıcının kendi pratiğini yürütmesini ve kaydetmesini sağlayan bir yazılım aracıdır. Platform ders, müfredat, sınav veya belge sunmaz; hiçbir unvan, yeterlilik ya da yetki kazandırmaz.",
          "Simüle edilen “hasta” gerçek bir danışan değildir; ürettiği yanıtlar yapay zekâ tarafından sentezlenir, hatalı, eksik, tutarsız veya yanıltıcı olabilir ve hiçbir klinik gerçeklik ya da doğruluk iddiası taşımaz.",
        ],
      },
      {
        heading: "2.1. Platformun Yerine Geçmediği Şeyler",
        bullets: [
          "Gerçek terapi ya da danışmanlık DEĞİLDİR; gerçek bir danışanla yürütülen hiçbir görüşmenin yerine geçmez.",
          "Klinik süpervizyonun YERİNE GEÇMEZ. Süpervizör yapay zekânın ürettiği geri bildirim raporları otomatik üretilmiş metinlerdir; uzman görüşü, resmî değerlendirme ya da not değildir.",
          "Tanı, tarama veya değerlendirme aracı DEĞİLDİR; Platformun hiçbir çıktısı gerçek bir kişi hakkında tanısal veya klinik karar amacıyla kullanılamaz.",
          "Kriz ve acil durumlarda KULLANILAMAZ. İntihar düşüncesi, kendine ya da bir başkasına zarar verme riski veya benzeri acil hâllerde Platform kapatılmalı ve derhâl yerel acil servislere ya da yetkili bir sağlık kuruluşuna başvurulmalıdır.",
          "Mesleki yeterlilik, ruhsat, unvan ya da yetki kaynağı DEĞİLDİR; Platformdaki kullanım süresi veya çıktıları hiçbir kurum önünde yeterlilik kanıtı olarak ileri sürülemez.",
        ],
      },
      {
        heading: "3. Kullanıcının Beyan ve Taahhütleri",
        bullets: [
          "Kullanıcı, Platformu yalnızca yasal ve etik sınırlar içinde, kendi mesleki pratiğini geliştirmek amacıyla kullanacağını taahhüt eder.",
          "Kullanıcı, Platforma GERÇEK hasta/danışan verisi, kimlik bilgisi veya herhangi bir kişiye ait özel nitelikli sağlık verisi GİRMEYECEĞİNİ kabul eder; aksi davranışın tüm hukuki sonuçları münhasıran Kullanıcıya aittir.",
          "Kullanıcı, Platformdan edindiği hiçbir çıktıyı gerçek bir kişiye dönük tanı, tedavi, müdahale veya klinik karar amacıyla kullanmayacağını taahhüt eder.",
          "Kullanıcı, kendi mesleki yeterliliğini, yetki sınırlarını ve ilgili meslek/etik mevzuatına uyumunu sağlamaktan bizzat sorumludur.",
          "Hesap güvenliği, parola gizliliği ve hesabı altında gerçekleşen tüm işlemlerin sorumluluğu Kullanıcıya aittir.",
        ],
      },
      {
        heading: "4. Sorumluluğun Tamamen Kullanıcıya Ait Olması (Feragat)",
        paras: [
          "Platform “OLDUĞU GİBİ” ve “MEVCUT HALİYLE” sunulur; açık veya zımni hiçbir garanti (kesintisizlik, hatasızlık, belirli bir amaca uygunluk, doğruluk, güncellik dâhil) verilmez.",
          "Kullanıcı; Platformu kullanması veya kullanamamasından, yapay zekâ çıktılarına güvenmesinden ya da bu çıktıları herhangi bir şekilde uygulamasından doğabilecek her türlü doğrudan, dolaylı, arızi, özel veya sonuç niteliğindeki zararın (maddi, manevi, mesleki itibar, veri kaybı, gelir kaybı, üçüncü kişilere verilen zararlar dâhil) tamamından MÜNHASIRAN ve ŞAHSEN sorumlu olduğunu kabul ve taahhüt eder.",
          "Kullanıcı, herhangi bir olumsuzluk hâlinde karşılaşabileceği her türlü zarara karşı Platforma, sahiplerine, çalışanlarına, geliştiricilerine, iş ortaklarına ve hizmet sağlayıcılarına yönelebilecek her türlü talep, dava ve tazminat hakkından — yürürlükteki mevzuatın izin verdiği en geniş ölçüde — şimdiden, geri dönülmez biçimde, gayrikabili rücu olarak FERAGAT ettiğini kabul, beyan ve taahhüt eder.",
          "Kullanıcı ayrıca, kendi kusur, ihmal veya bu koşullara aykırı kullanımı nedeniyle Platformun herhangi bir üçüncü kişi ya da kurum tarafından muhatap alınması hâlinde, Platformu her türlü talep, zarar, ceza ve masraftan (avukatlık ücretleri dâhil) tamamen ârî kılacağını (tazmin/garanti) taahhüt eder.",
        ],
      },
      {
        heading: "5. Sorumluluğun Sınırlandırılması",
        paras: [
          "Yürürlükteki hukukun emredici hükümleri saklı kalmak kaydıyla, Platformun toplam yasal sorumluluğu — her hâlükârda — Kullanıcının ilgili talebe konu olaydan önceki 12 ayda Platforma fiilen ödediği ücret tutarı ile sınırlıdır. Ücretsiz kullanım hâlinde bu tutar sıfırdır.",
        ],
      },
      {
        heading: "6. Fikrî Mülkiyet, Değişiklik ve Fesih",
        bullets: [
          "Platform, yazılımı, tasarımı, hasta vaka şablonları ve içerikleri üzerindeki tüm fikrî mülkiyet hakları Platforma aittir.",
          "Kullanıcının Platform üzerinde oluşturduğu içerik (yazdığı terapist mesajları, seans transkriptleri ve bunlardan türeyen materyaller dâhil) üzerindeki devredilebilir tüm mali fikrî mülkiyet hakları, içeriğin oluşturulduğu anda münhasıran, süresiz ve bedelsiz olarak Platforma devredilmiş sayılır; Platform bu içeriği hizmetin sunulması, geri bildirim üretimi ile sistemin geliştirilmesi ve eğitilmesi dâhil her amaçla dünya çapında, sınırsız biçimde kullanabilir, çoğaltabilir ve işleyebilir. Kullanıcının yürürlükteki veri koruma mevzuatından (GDPR / KVKK) doğan kişisel veri hakları bu devirden etkilenmez ve saklıdır.",
          "Platform, bu koşulları ve hizmet kapsamını dilediği zaman, önceden bildirmeksizin değiştirme, askıya alma veya sonlandırma hakkını saklı tutar.",
          "Koşullara aykırılık hâlinde Platform, hesabı önceden bildirimde bulunmaksızın askıya alabilir veya kapatabilir.",
        ],
      },
      {
        heading: "7. Uygulanacak Hukuk ve Yetki",
        paras: [
          "Bu koşullar Türkiye Cumhuriyeti hukukuna tabidir. Doğabilecek uyuşmazlıklarda Türkiye Cumhuriyeti mahkemeleri ve icra daireleri yetkilidir.",
          "Avrupa Birliği'nde yerleşik tüketiciler bakımından, bulundukları ülkenin emredici tüketici koruma hükümlerinden ve GDPR kapsamındaki veri koruma haklarından doğan korumalar saklıdır; bu hukuk seçimi söz konusu emredici hükümleri bertaraf etmez.",
        ],
      },
    ],
  },
  {
    id: "privacy",
    title: "Gizlilik Politikası",
    description:
      "Temel standart olarak GDPR; işlenen veriler, hukuki sebepler, AB'de barındırma, uluslararası aktarım (AI sağlayıcı) ve Türkiye'deki kullanıcılar için KVKK md. 9 yurt dışı aktarım maddesi.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          `Bu Gizlilik Politikası, ${OWNER_TR} tarafından işletilen Platform kapsamında kişisel verilerin nasıl işlendiğini açıklar. Politika, temel veri koruma standardı olarak Avrupa Birliği Genel Veri Koruma Tüzüğü'nü (GDPR / 2016/679) esas alır; Türkiye'de bulunan kullanıcılar için ek olarak 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) — özellikle yurt dışı aktarıma ilişkin md. 9 — uygulanır. KVKK Aydınlatma Metni ve Açık Rıza Metni bu politikanın ayrılmaz parçalarıdır.`,
        ],
      },
      {
        heading: "Veri sorumlusu ve barındırma yeri",
        bullets: [
          "Veri sorumlusu (data controller): PsyPractice. İlgili kişi/veri sahibi başvuruları için Platformda ilan edilen iletişim kanalı kullanılır (iletişim e-postası: info@psycora.com).",
          "Barındırma: Platform ve veritabanı, Avrupa Birliği içinde — Hollanda'daki bir veri merkezinde (Oracle Cloud) barındırılır. AB/AEA'da bulunan kullanıcılar bakımından veriler AB içinde işlenir ve saklanır.",
          "Barındırma sağlayıcısı yalnızca veri işleyen (processor) sıfatıyla, akdedilen Veri İşleme Sözleşmesi (DPA) çerçevesinde hareket eder.",
        ],
      },
      {
        heading: "İşlenen veriler",
        bullets: [
          "Hesap verileri: ad, e-posta, şifrelenmiş parola, abonelik/plan bilgisi.",
          "Kullanım verileri: oluşturulan seanslar, seans transkriptleri, üretilen hasta vakaları, süpervizör geri bildirim raporları.",
          "Teknik veriler: oturum kayıtları, IP, tarayıcı/locale tercihi, hız sınırı (rate-limit) ve güvenlik kayıtları.",
          "İsteğe bağlı: Kullanıcının kendi sağladığı Anthropic API anahtarı (şifreli saklanır).",
        ],
      },
      {
        heading: "İşleme amaçları ve hukuki sebep (GDPR md. 6)",
        bullets: [
          "Sözleşmenin ifası (md. 6/1-b): hesap oluşturma, seans yürütme, çok-seanslı hasta hafızası, süpervizör değerlendirmesi, abonelik ve kullanım limiti yönetimi.",
          "Meşru menfaat (md. 6/1-f): güvenlik, kötüye kullanımın önlenmesi, sistem iyileştirme ve hata ayıklama.",
          "Hukuki yükümlülük (md. 6/1-c): mevzuattan doğan saklama/raporlama yükümlülükleri.",
          "Açık rıza (md. 6/1-a): hizmet için zorunlu olmayan analitik/ürün iyileştirme işlemeleri ve — gerektiğinde — AB/AEA dışına aktarım.",
        ],
      },
      {
        heading: "Uluslararası aktarım",
        paras: [
          "Barındırma AB içinde (Hollanda) olduğundan, AB/AEA kullanıcılarının verileri kural olarak AB içinde işlenir ve burada üçüncü ülkeye aktarım söz konusu değildir. Ancak yapay zekâ yanıtlarının üretilmesi için seans içerikleri ve istemler yapay zekâ hizmet sağlayıcısına (Anthropic) iletilir; bu sağlayıcının işleme altyapısı AB/AEA dışında (ör. ABD) bulunabilir.",
          "AB/AEA dışına yapılan bu aktarım, GDPR md. 44 vd. uyarınca uygun güvencelere — özellikle Avrupa Komisyonu Standart Sözleşme Hükümleri'ne (SCC) — dayandırılır ve sağlayıcılarla veri işleme sözleşmesi (DPA) akdedilir. Platform kişisel verileri pazarlama amacıyla SATMAZ; veriler hizmetin işletilmesi için zorunlu olanlar dışında üçüncü kişilerle paylaşılmaz (yasal mercilerin mevzuata uygun talepleri saklıdır).",
        ],
      },
      {
        heading: "Türkiye'deki kullanıcılar için — KVKK md. 9 (yurt dışı aktarım)",
        paras: [
          "Türkiye'de bulunan kullanıcıların kişisel verileri, barındırmanın Hollanda'da (AB) olması nedeniyle Türkiye dışına aktarılır; ayrıca yapay zekâ işleme için Anthropic'e aktarılabilir. Bu aktarımlar KVKK md. 9 kapsamındadır ve uygun güvencelere (Kurul'un yayımladığı standart sözleşme / taahhütname) veya — gereken hâllerde — açık rızanıza dayanır. Aktarımın kapsamı, alıcıları ve amacı KVKK Aydınlatma Metni ile Açık Rıza Metni'nde ayrıntılı olarak açıklanır.",
        ],
      },
      {
        heading: "Veri sahibi hakları (GDPR md. 15–22)",
        bullets: [
          "Erişim, düzeltme, silme ('unutulma hakkı') ve işlemenin kısıtlanmasını isteme.",
          "Veri taşınabilirliği: sağladığınız verileri yapılandırılmış, yaygın kullanılan, makinece okunabilir biçimde alma.",
          "İtiraz: meşru menfaate dayalı işlemeye ve doğrudan pazarlamaya itiraz etme.",
          "Yalnızca otomatik işlemeye dayalı bireysel karar/profillemeye ilişkin haklar.",
          "Rızaya dayalı işlemelerde rızayı dilediğiniz an geri çekme (geri çekme, o ana kadarki işlemenin hukukiliğini etkilemez).",
          "Bir denetim makamına şikâyet hakkı: AB'de ilgili veri koruma otoritesi; Türkiye'de Kişisel Verileri Koruma Kurumu.",
        ],
      },
      {
        heading: "Saklama ve güvenlik",
        bullets: [
          "Veriler işleme amacının gerektirdiği süre ve ilgili mevzuatın öngördüğü süreler boyunca saklanır; süre dolduğunda silinir veya anonimleştirilir.",
          "Hassas alanlar (ör. Kullanıcı API anahtarı, parola) şifreli/hash'li tutulur; erişim yetki bazlıdır.",
          "Hiçbir aktarım veya saklama yöntemi %100 güvenli olmadığından mutlak güvenlik garanti edilemez.",
        ],
      },
      {
        heading: "Gerçek hasta verisi yasağı",
        paras: [
          "Platform bir simülasyon yazılımıdır; gerçek danışanlara ait kişisel/sağlık verisi girilmesi yasaktır. Kullanıcının bu yasağa aykırı veri girmesinden doğacak tüm sorumluluk münhasıran Kullanıcıya aittir.",
        ],
      },
    ],
  },
  {
    id: "kvkk",
    title: "KVKK Aydınlatma Metni",
    description:
      "6698 sayılı Kanun md. 10 kapsamında veri sorumlusu, işleme amaçları, hukuki sebep, aktarım ve ilgili kişi hakları.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "Veri Sorumlusu",
        paras: [
          `6698 sayılı Kişisel Verilerin Korunması Kanunu (“KVKK”) md. 10 uyarınca, kişisel verileriniz Veri Sorumlusu sıfatıyla ${OWNER_TR} tarafından aşağıda açıklanan kapsamda işlenmektedir.`,
        ],
      },
      {
        heading: "İşlenen veriler ve amaçları",
        bullets: [
          "Kimlik/İletişim (ad, e-posta): hesap oluşturma, kimlik doğrulama, iletişim.",
          "Müşteri işlem (abonelik, plan, kullanım): hizmet sunumu, faturalandırma, limit uygulaması.",
          "İşlem güvenliği (oturum, IP, log): güvenlik, kötüye kullanımın önlenmesi, hukuki yükümlülük.",
          "Kullanıcı içerikleri (seans transkripti, üretilen vakalar): yazılım hizmetinin yürütülmesi ve geri bildirim üretimi.",
          "Kurumsal kohort üyeliği (kohort kimliği, üyelik süresi): kullanıcının bir kuruma bağlı kohorta katılması hâlinde, kohort eğitmeninin görüşü ve kohortun yürütülmesi.",
        ],
      },
      {
        heading: "Hukuki sebep (md. 5)",
        bullets: [
          "Sözleşmenin kurulması/ifası: hizmetin sunulması için zorunlu veriler.",
          "Veri sorumlusunun meşru menfaati: güvenlik, dolandırıcılık önleme, sistem iyileştirme.",
          "Hukuki yükümlülük: mevzuattan doğan saklama/raporlama.",
          "Açık rıza: yurt dışı aktarım (md. 9) ve hizmet için zorunlu olmayan ek işlemeler.",
        ],
      },
      {
        heading: "Yurt dışı aktarım (md. 9)",
        paras: [
          "Platform ve veritabanı Avrupa Birliği içinde — Hollanda'daki bir veri merkezinde (Oracle Cloud) barındırıldığından, Türkiye'de bulunan kullanıcıların kişisel verileri Türkiye dışına (AB'ye) aktarılır. Ayrıca, yapay zekâ yanıtlarının üretilmesi amacıyla seans içerikleri ve istemler, işleme altyapısı AB/AEA dışında (ör. ABD) bulunabilen yapay zekâ hizmet sağlayıcısına (Anthropic) aktarılabilir.",
          "Bu yurt dışı aktarımlar KVKK md. 9 kapsamındadır ve uygun güvencelere — Kurul'un yayımladığı standart sözleşme / taahhütname gibi — ya da gerektiğinde açık rızanıza dayanır. Barındırma ve hizmet sağlayıcılarıyla veri işleme sözleşmeleri (DPA) akdedilir.",
        ],
      },
      {
        heading: "Kurumsal kohort ve eğitmen görünürlüğü",
        paras: [
          "Bir üniversite, klinik ya da benzeri bir kurumun açtığı kohorta katılmanız hâlinde, kohort süresi boyunca seans içerikleriniz (transkriptler) ve süpervizör raporlarınız o kohortun eğitmeni tarafından görülebilir. Bu görünürlük yalnızca kohorta bağlı seanslarınızı kapsar ve kohort üyeliğiniz sona erdiğinde ortadan kalkar.",
          "Bu erişim, kohorta katılırken karşınıza çıkan AYRI bir açık rıza ekranıyla alınır; Platformun genel kullanım onayına dâhil değildir. Rıza vermezseniz kohorta katılamazsınız, ancak Platformu bireysel olarak kullanmaya aynı şekilde devam edersiniz. Rızanızı geri almanız hâlinde kohort üyeliğiniz sona erer, bireysel kullanımınız etkilenmez.",
        ],
      },
      {
        heading: "İlgili kişi hakları (md. 11)",
        bullets: [
          "Kişisel verinizin işlenip işlenmediğini öğrenme, işlenmişse bilgi talep etme.",
          "İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme.",
          "Yurt içi/yurt dışı aktarılan üçüncü kişileri bilme.",
          "Eksik/yanlış işlenmişse düzeltilmesini, şartlar oluştuğunda silinmesini/yok edilmesini isteme.",
          "İşlemenin münhasıran otomatik sistemlerle analizi sonucu aleyhe bir sonuç çıkmasına itiraz etme.",
          "Kanuna aykırı işleme nedeniyle zarara uğranması hâlinde zararın giderilmesini talep etme.",
        ],
      },
      {
        heading: "Başvuru",
        paras: [
          "Haklarınıza ilişkin taleplerinizi Veri Sorumlusunun ilan ettiği iletişim kanalı üzerinden iletebilirsiniz. Başvurular KVKK ve ilgili ikincil düzenlemelerde öngörülen sürelerde sonuçlandırılır.",
        ],
      },
    ],
  },
  {
    id: "consent",
    title: "Açık Rıza Metni",
    description:
      "Aydınlatma metninden ayrı; yurt dışı aktarım ve AI işlemeye yönelik özgür iradeyle verilen onay.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          "KVKK Aydınlatma Metni'ni okuyup anladığımı; aşağıdaki işlemelere özgür irademle, bilgilendirilmiş şekilde ve açık biçimde rıza gösterdiğimi beyan ederim. (KVKK Kurulu'nun 2018/90 sayılı kararı uyarınca açık rıza, aydınlatma yükümlülüğünden ayrı olarak alınır.)",
        ],
      },
      {
        heading: "Rıza verilen işlemeler",
        bullets: [
          "Kişisel verilerimin, Platformun Avrupa Birliği'nde (Hollanda / Oracle Cloud) barındırılması nedeniyle Türkiye dışına aktarılması ve orada saklanıp işlenmesi (KVKK md. 9).",
          "Seans transkriptlerim ve istemlerimin, yapay zekâ yanıtı üretilmesi amacıyla işleme altyapısı AB/AEA dışında bulunabilen yapay zekâ hizmet sağlayıcısına (Anthropic) aktarılması ve orada işlenmesi (KVKK md. 9).",
          "Hizmetin sunulması için zorunlu olmayan; ürün iyileştirme ve analitik amaçlı işlemeler.",
          "Tarafıma hizmetle ilgili bilgilendirme yapılması.",
        ],
      },
      {
        heading: "Rızanın geri alınması",
        paras: [
          "Açık rızamı dilediğim zaman geri alabileceğimi; ancak yurt dışı AI aktarımı hizmetin teknik olarak işlemesi için zorunlu olduğundan, bu rızayı geri almamın ilgili özellikleri ya da hizmetin tamamını kullanamamam sonucunu doğurabileceğini anladığımı kabul ederim. Geri alma, geri alma tarihine kadar gerçekleştirilen işlemelerin hukukiliğini etkilemez.",
        ],
      },
    ],
  },
  {
    // Bu belge PLATFORM KULLANICISI için değil, kurumsal tanıtım e-postası alan
    // akademisyen/kurum yetkilisi için yazılmıştır. KVKK md. 10 + Aydınlatma
    // Tebliği md. 6: veri ilgili kişiden elde edilmediğinde aydınlatma EN GEÇ
    // ilk iletişim anında yapılır — yani tanıtım e-postasının kendisi bu metne
    // link vermek zorundadır (/legal/public#outreach). Onay ekranına GİRMEZ
    // (consent ekranı yalnızca kvkk + consent belgelerini gösterir).
    id: "outreach",
    title: "Tanıtım İletişimi Aydınlatma Metni",
    description:
      "Kurumsal tanıtım e-postası alan akademisyen ve kurum yetkilileri için: verinin kaynağı, işleme amacı, hukuki sebebi ve listeden çıkma yolu.",
    updated: "2026-08-06",
    sections: [
      {
        paras: [
          "Bu metin, PsyPractice tarafından üniversitelerin psikoloji bölümlerine ve akademik kadrolarına yönelik gönderilen kurumsal tanıtım e-postalarının alıcılarını, 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) md. 10 ve Aydınlatma Yükümlülüğünün Yerine Getirilmesinde Uyulacak Usul ve Esaslar Hakkında Tebliğ uyarınca bilgilendirmek amacıyla hazırlanmıştır. Kişisel verileriniz doğrudan sizden elde edilmediğinden, aydınlatma en geç sizinle kurulan ilk iletişim anında yapılır; bu metne tanıtım e-postasının içindeki bağlantıdan ulaşmış olmanızın sebebi budur.",
        ],
      },
      {
        heading: "1. Veri Sorumlusu",
        bullets: [
          `Unvan: ${MERCHANT.legalName} (${MERCHANT.type})`,
          `Adres: ${MERCHANT.address}`,
          `Vergi Dairesi / No: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}`,
          `E-posta: ${MERCHANT.email}`,
          `Web: ${MERCHANT.website}`,
        ],
      },
      {
        heading: "2. İşlenen kişisel veriler ve kaynağı",
        paras: [
          "İşlenen veriler yalnızca mesleki/kurumsal iletişim verileriyle sınırlıdır: ad-soyad, akademik unvan, görev yapılan üniversite ve bölüm, kurumsal e-posta adresi ve varsa kurumsal telefon numarası.",
          "Bu veriler sizden değil, üniversitelerin resmî bölüm ve akademik kadro sayfalarında herkese açık biçimde yayımlanmış hâllerinden derlenmiştir. Özel nitelikli kişisel veri işlenmez; kişisel (kurumsal olmayan) e-posta adresleri tanıtım amacıyla kullanılmaz.",
        ],
      },
      {
        heading: "3. İşleme amacı",
        paras: [
          "Verileriniz, psikoloji eğitiminde kullanılmak üzere geliştirilen bir yazılım aracının kurumsal tanıtımının yapılması, bölümünüzle olası bir pilot uygulama veya iş birliği görüşmesine davet edilmeniz ve bu görüşmeye ilişkin yazışmaların yürütülmesi amaçlarıyla işlenir. Başka bir amaçla kullanılmaz, satılmaz ve pazarlama amacıyla üçüncü kişilere aktarılmaz.",
        ],
      },
      {
        heading: "4. Hukuki sebep",
        paras: [
          "İşleme, KVKK md. 5/2-(f) uyarınca, temel hak ve özgürlüklerinize zarar vermemek kaydıyla veri sorumlusunun meşru menfaatine dayanır: iletişim yalnızca kurumsal adrese, yalnızca muhatabın mesleki faaliyet alanıyla doğrudan ilgili bir konuda ve sınırlı sayıda kurulur.",
          "Verinin internette herkese açık olması tek başına bir hukuki sebep sayılmaz. Kişisel Verileri Koruma Kurulu, bir verinin alenî olmasının onu her amaç için kullanılabilir kılmadığını, alenileştirme iradesinin ve amacının ayrıca değerlendirilmesi gerektiğini belirtmiştir; bu nedenle md. 5/2-(d) hukuki sebebine dayanılmamaktadır.",
        ],
      },
      {
        heading: "5. Aktarım ve saklama",
        paras: [
          "İletişim, altyapısı yurt dışında bulunabilen e-posta hizmet sağlayıcısı üzerinden yürütülür; bu kapsamdaki yurt dışı aktarımlar KVKK md. 9 çerçevesinde uygun güvenceler ile gerçekleştirilir.",
          "Verileriniz, iletişimin amacı ortadan kalktığında veya talebiniz üzerine silinir. Silme talebinden sonra size bir daha ulaşılmaması için, yalnızca bu amaçla sınırlı bir 'iletişim kurulmayacaklar' kaydı tutulur; bu kayıt tanıtım için kullanılamaz.",
        ],
      },
      {
        heading: "6. Ticari elektronik ileti ve ret hakkı",
        paras: [
          "Tanıtım niteliğindeki iletiler 6563 sayılı Elektronik Ticaretin Düzenlenmesi Hakkında Kanun kapsamındadır. Hiçbir gerekçe göstermeksizin ileti almayı reddedebilirsiniz; ret talebiniz en geç üç iş günü içinde işleme alınır ve tarafınıza bir daha tanıtım iletisi gönderilmez. Ret için gönderilen e-postayı yanıtlamanız ya da " + MERCHANT.email + " adresine yazmanız yeterlidir.",
        ],
      },
      {
        heading: "7. Haklarınız",
        paras: [
          "KVKK md. 11 uyarınca; kişisel verilerinizin işlenip işlenmediğini öğrenme, işlenmişse buna ilişkin bilgi talep etme, işleme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme, yurt içinde/yurt dışında aktarıldığı üçüncü kişileri bilme, eksik veya yanlış işlenmiş olması hâlinde düzeltilmesini isteme, silinmesini veya yok edilmesini isteme, yapılan işlemlerin aktarıldığı üçüncü kişilere bildirilmesini isteme, münhasıran otomatik sistemlerle analiz edilmesi suretiyle aleyhinize bir sonuç ortaya çıkmasına itiraz etme ve zarara uğramanız hâlinde zararın giderilmesini talep etme haklarına sahipsiniz.",
          `Taleplerinizi ${MERCHANT.email} adresine iletebilirsiniz; başvurularınız en geç otuz gün içinde sonuçlandırılır. Ayrıca Kişisel Verileri Koruma Kurulu'na şikâyette bulunma hakkınız saklıdır.`,
        ],
      },
    ],
  },
  {
    id: "cookies",
    title: "Çerez (Cookie) Politikası",
    description: "Kullanılan çerez türleri, amaçları ve tercih yönetimi.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          "Platform, oturumun sürdürülmesi ve tercihlerinizin hatırlanması için çerez ve benzeri yerel depolama (localStorage) teknolojilerini kullanır.",
        ],
      },
      {
        heading: "Kullanılan teknolojiler",
        bullets: [
          "Zorunlu: oturum/kimlik doğrulama jetonu, güvenlik. Bunlar olmadan hizmet çalışmaz.",
          "İşlevsel: dil/locale tercihi (NEXT_LOCALE), arayüz tercihleri (ör. tema).",
          "Performans/güvenlik: hız sınırı ve kötüye kullanım tespiti için teknik kayıtlar.",
        ],
      },
      {
        heading: "Tercih yönetimi",
        paras: [
          "Tarayıcınızdan çerezleri silebilir veya engelleyebilirsiniz; ancak zorunlu çerezlerin engellenmesi oturum açma ve temel işlevlerin çalışmamasına yol açar. GDPR / ePrivacy ilkeleri ve KVKK Çerez Rehberi doğrultusunda, zorunlu olmayan çerezler yalnızca açık tercihiniz/onayınız doğrultusunda kullanılır.",
        ],
      },
    ],
  },
  {
    id: "about",
    title: "Hakkımızda",
    description:
      "PsyPractice'in amacı, sunduğu hizmet ve işletme (satıcı) iletişim bilgileri.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          "PsyPractice, psikoloji öğrencileri ve yeni mezun klinisyen adayları için geliştirilmiş, yapay zekâ destekli bir hasta simülasyonu ve klinik beceri geliştirme platformudur. Kullanıcılar metin tabanlı terapi seansları yürütür; her seansın sonunda bir süpervizör yapay zekâ, görüşmeyi değerlendirerek gelişimsel bir geri bildirim raporu üretir. Hasta yapay zekâsı, seanslar arası hafızayla terapötik ilişkiyi seans seans taşır.",
        ],
      },
      {
        heading: "Misyonumuz",
        paras: [
          "Amacımız, geleceğin ruh sağlığı uzmanlarına; gerçek danışanlara zarar verme riski olmadan, güvenli ve tekrarlanabilir bir ortamda görüşme pratiği yapma imkânı sunmaktır. PsyPractice bir eğitim veya öğretim hizmeti değil, kullanıcının kendi pratiğini yürütmesini ve kaydetmesini sağlayan bir yazılım aracıdır. Gerçek terapi ya da danışmanlık değildir, klinik süpervizyonun yerine geçmez, tanı veya değerlendirme aracı değildir ve kriz ile acil durumlarda kullanılamaz.",
        ],
      },
      {
        heading: "Ne sunuyoruz",
        bullets: [
          "Klinik literatürden sentezlenmiş çeşitli hasta profilleriyle metin tabanlı terapi seansı simülasyonu.",
          "Seans sonrası otomatik süpervizör değerlendirme raporları.",
          "Seanslar arası hafıza taşıyan, terapötik ilişkiyi geliştiren 'yaşayan hasta' modeli.",
          "Öğrencinin kendi vakasını yapay zekâ ile üretebildiği özel hasta oluşturma.",
          "Mikrobeceri gelişimini izleyen ilerleme metrikleri.",
        ],
      },
      {
        heading: "Satıcı / İşletme Bilgileri",
        bullets: [
          `Unvan: ${MERCHANT.legalName} (${MERCHANT.type})`,
          `Adres: ${MERCHANT.address}`,
          `Vergi Dairesi / No: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}`,
          `E-posta: ${MERCHANT.email}`,
          `Web: ${MERCHANT.website}`,
        ],
      },
    ],
  },
  {
    id: "distance-sales",
    title: "Mesafeli Satış Sözleşmesi",
    description:
      "6502 sayılı Kanun ve Mesafeli Sözleşmeler Yönetmeliği kapsamında satıcı, alıcı, hizmet, bedel, ödeme, ifa ve cayma hakkı koşulları.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "1. Taraflar",
        bullets: [
          `SATICI: ${MERCHANT.legalName} (${MERCHANT.type}). Adres: ${MERCHANT.address}. Vergi Dairesi/No: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}. E-posta: ${MERCHANT.email}.`,
          "ALICI: Platformda üyelik oluşturarak ücretli abonelik satın alan gerçek/tüzel kişi (bundan böyle \"Alıcı\").",
        ],
      },
      {
        heading: "2. Sözleşmenin Konusu",
        paras: [
          "İşbu Sözleşmenin konusu, Alıcının Platform üzerinden elektronik ortamda satın aldığı, aşağıda nitelikleri ve satış bedeli belirtilen dijital hizmet aboneliğinin satışı ve ifasına ilişkin olarak tarafların 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği hükümleri uyarınca hak ve yükümlülüklerinin belirlenmesidir.",
        ],
      },
      {
        heading: "3. Hizmetin Niteliği ve Bedeli",
        bullets: [
          "Satışa konu hizmet, PsyPractice yazılımının ücretli abonelik planı kapsamında sunulan yapay zekâ destekli hasta simülasyonu özellikleridir (dijital hizmet / yazılım kullanım hakkı).",
          "PsyPractice bir eğitim veya öğretim hizmeti değil, kullanıcının kendi pratiğini yürütmesini ve kaydetmesini sağlayan bir yazılım aracıdır. Abonelik bedeli, bu yazılıma erişim hakkının karşılığıdır; ders, müfredat, sınav, danışmanlık ya da belge bedeli değildir.",
          "Plan içeriği, süresi (aylık/yıllık) ve güncel bedeli, satın alma anında Platformun fiyatlandırma/ödeme ekranında açıkça gösterilir; vergiler dâhil toplam tutar ödeme öncesinde Alıcıya beyan edilir.",
          "Fiyatlar Platformda ilan edilen para biriminde geçerlidir; abonelik, iptal edilmediği sürece seçilen dönem sonunda aynı bedelle otomatik yenilenir.",
        ],
      },
      {
        heading: "4. Ödeme",
        paras: [
          "Ödemeler, lisanslı ödeme kuruluşu iyzico altyapısı üzerinden kredi/banka kartı ile güvenli biçimde tahsil edilir. Kart bilgileri Satıcı tarafından saklanmaz; ödeme işlemi iyzico'nun güvenli sistemleri üzerinden gerçekleştirilir. Yinelenen (abonelik) ödemelerde bedel, her yenileme döneminde Alıcının onayladığı kart üzerinden otomatik tahsil edilir.",
        ],
      },
      {
        heading: "5. İfa ve Teslimat",
        paras: [
          "Hizmet dijital niteliktedir ve fiziksel teslimat içermez. Ödemenin onaylanmasının ardından abonelik özellikleri Alıcının hesabında derhâl (anında) erişime açılır. İfa, elektronik ortamda ve ilgili abonelik süresi boyunca kesintisiz erişim sağlanarak gerçekleşir.",
        ],
      },
      {
        heading: "6. Cayma Hakkı ve İstisnası",
        bullets: [
          "Mesafeli Sözleşmeler Yönetmeliği md. 15/1-(ğ) uyarınca, elektronik ortamda anında ifa edilen ve gayrimaddi (dijital) mallar/hizmetler bakımından, Alıcının onayı ile ifaya başlanmış olması hâlinde cayma hakkı kullanılamaz.",
          "Alıcı, satın alma sırasında hizmetin anında ifa edileceğini ve bu nedenle cayma hakkının bulunmadığını kabul eder. Aboneliğini dilediği zaman iptal ederek yenilenmesini durdurabilir; iptal, içinde bulunulan ödenmiş dönemin sonunda yürürlüğe girer.",
          "İadeye ilişkin ayrıntılı koşullar için bkz. \"Teslimat ve İade Şartları\".",
        ],
      },
      {
        heading: "7. Uyuşmazlık ve Yürürlük",
        paras: [
          "Alıcı, uyuşmazlık hâlinde Ticaret Bakanlığınca ilan edilen parasal sınırlar dâhilinde yerleşim yerindeki Tüketici Hakem Heyetine veya Tüketici Mahkemelerine başvurabilir. İşbu Sözleşme, Alıcının elektronik ortamda onayı ile kurulur ve yürürlüğe girer.",
        ],
      },
    ],
  },
  {
    id: "delivery-refund",
    title: "Teslimat ve İade Şartları",
    description:
      "Dijital hizmetin teslimi, abonelik iptali, cayma istisnası ve ücret iadesi koşulları.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "Teslimat (Dijital Hizmet)",
        bullets: [
          "PsyPractice tamamen dijital bir hizmettir; fiziksel ürün gönderimi ve kargo teslimatı yoktur.",
          "Ödeme onaylandığı anda abonelik özellikleri Alıcının hesabında otomatik ve anında etkinleşir; ayrı bir teslimat süresi veya teslimat ücreti bulunmaz.",
          "Hizmete internet bağlantısı olan herhangi bir cihazın web tarayıcısı üzerinden erişilir.",
        ],
      },
      {
        heading: "Abonelik İptali",
        bullets: [
          "Alıcı, aboneliğini dilediği zaman hesap ayarlarından iptal edebilir. İptal, mevcut ödenmiş dönemin sonunda yürürlüğe girer ve sonraki otomatik yenilemeyi durdurur.",
          "İptal sonrası, ödenmiş dönemin kalan süresi boyunca hizmete erişim devam eder.",
        ],
      },
      {
        heading: "Cayma Hakkı ve İade",
        bullets: [
          "Hizmet, Alıcının onayıyla anında ifa edilen dijital bir hizmet olduğundan, Mesafeli Sözleşmeler Yönetmeliği md. 15/1-(ğ) uyarınca kural olarak cayma hakkı ve buna bağlı iade hakkı doğmaz.",
          "Hatalı/çift çekim, teknik nedenlerle hizmete hiç erişilememesi veya benzeri açık kusur hâllerinde Alıcı, ödeme tarihinden itibaren makul süre içinde satıcıya başvurarak iade talep edebilir.",
          `İade talepleri ${MERCHANT.email} adresine iletilir; haklı bulunan iadeler, ödemenin yapıldığı karta iyzico altyapısı üzerinden, kural olarak 14 gün içinde iade edilir. İade süresi bankanın işlem süresine bağlı olarak değişebilir.`,
        ],
      },
      {
        heading: "İletişim",
        paras: [
          `Teslimat, iptal ve iade konularındaki tüm talepleriniz için: ${MERCHANT.email}${MERCHANT.phone ? " · " + MERCHANT.phone : ""}.`,
        ],
      },
    ],
  },
];

// ── English ──────────────────────────────────────────────────────────────────
const DOCS_EN: LegalDoc[] = [
  {
    id: "terms",
    title: "Terms of Use & Disclaimer",
    description:
      "Nature and limits of the service, what it is not a substitute for, user undertakings, and a comprehensive waiver placing all liability on the user.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "1. Parties and Acceptance",
        paras: [
          `These Terms of Use are entered into between ${OWNER_EN} and any natural or legal person who accesses or uses the Platform (the "User"). By creating an account, signing in, or using the Platform in any manner, the User confirms that they have read, understood, and unconditionally accept and undertake these Terms in full. Anyone who does not accept these Terms must not use the Platform.`,
        ],
      },
      {
        heading: "2. Nature and Limits of the Service",
        bullets: [
          "PsyPractice is an AI-assisted patient SIMULATION software that lets psychology students and early-career clinicians carry out and record their own interviewing practice in a safe environment.",
          "PsyPractice is not an education or teaching service; it is a software tool that lets the user carry out and record their own practice. The Platform provides no lessons, curriculum, examinations, or documents, and confers no title, qualification, or authorisation whatsoever.",
          "The simulated “patient” is not a real client; its responses are synthesized by artificial intelligence, may be erroneous, incomplete, inconsistent, or misleading, and carry no claim of clinical accuracy or truth.",
        ],
      },
      {
        heading: "2.1. What the Platform Is Not a Substitute For",
        bullets: [
          "It is NOT real therapy or counselling, and it substitutes for no session conducted with a real client.",
          "It is NOT a substitute for clinical supervision. Feedback reports produced by the supervisor AI are automatically generated text; they are not expert opinion, an official assessment, or a grade.",
          "It is NOT a diagnostic, screening, or assessment instrument; no output of the Platform may be used for diagnostic or clinical decision-making about any real person.",
          "It MUST NOT be used in crises or emergencies. In cases of suicidal ideation, risk of harm to oneself or others, or any comparable emergency, close the Platform and contact your local emergency services or a competent health institution immediately.",
          "It is NOT a source of professional competence, licensure, title, or authority; time spent on the Platform and its outputs may not be presented to any institution as proof of qualification.",
        ],
      },
      {
        heading: "3. User Representations and Undertakings",
        bullets: [
          "The User undertakes to use the Platform solely within lawful and ethical bounds, for the purpose of developing their own professional practice.",
          "The User agrees NOT to enter any REAL patient/client data, identifying information, or special-category health data belonging to any person; all legal consequences of doing so rest exclusively with the User.",
          "The User undertakes not to use any output obtained from the Platform for the diagnosis, treatment, intervention, or clinical decision-making concerning any real person.",
          "The User is solely responsible for their own professional competence, scope of authority, and compliance with applicable professional and ethical regulations.",
          "Account security, password confidentiality, and all activity carried out under the User's account are the User's responsibility.",
        ],
      },
      {
        heading: "4. All Liability Rests with the User (Waiver)",
        paras: [
          "The Platform is provided “AS IS” and “AS AVAILABLE”, without any express or implied warranty whatsoever (including uninterrupted operation, error-freeness, fitness for a particular purpose, accuracy, or currency).",
          "The User accepts and undertakes that they are EXCLUSIVELY and PERSONALLY responsible for any and all direct, indirect, incidental, special, or consequential damages (including material, moral, professional-reputation, data-loss, revenue-loss, and damages caused to third parties) arising from their use of, or inability to use, the Platform, from relying on AI outputs, or from applying such outputs in any way.",
          "To the fullest extent permitted by applicable law, the User hereby irrevocably and unconditionally WAIVES — now and in advance — any and all claims, lawsuits, and rights to compensation that they might raise against the Platform, its owners, employees, developers, business partners, and service providers in respect of any harm they may face in the event of any adverse outcome.",
          "The User further undertakes to hold the Platform fully harmless (indemnify) from any and all claims, damages, penalties, and costs (including attorneys' fees) should the Platform be approached by any third party or institution due to the User's own fault, negligence, or use in breach of these Terms.",
        ],
      },
      {
        heading: "5. Limitation of Liability",
        paras: [
          "Without prejudice to the mandatory provisions of applicable law, the Platform's total legal liability shall in any event be limited to the amount actually paid by the User to the Platform in the 12 months preceding the event giving rise to the relevant claim. Where use is free of charge, this amount is zero.",
        ],
      },
      {
        heading: "6. Intellectual Property, Modification, and Termination",
        bullets: [
          "All intellectual property rights in the Platform, its software, design, patient case templates, and content belong to the Platform.",
          "All transferable economic intellectual-property rights in any content the User creates on the Platform (including the therapist messages they write, session transcripts, and materials derived therefrom) are deemed assigned to the Platform exclusively, perpetually, and royalty-free at the moment such content is created; the Platform may use, reproduce, and process this content worldwide and without restriction for any purpose, including operating the service, generating feedback, and improving and developing the system (including machine-learning improvement). The User's personal-data rights under applicable data-protection law (GDPR / KVKK) are unaffected by this assignment and remain reserved.",
          "The Platform reserves the right to modify, suspend, or discontinue these Terms and the scope of the service at any time, without prior notice.",
          "In the event of a breach of these Terms, the Platform may suspend or close the account without prior notice.",
        ],
      },
      {
        heading: "7. Governing Law and Jurisdiction",
        paras: [
          "These Terms are governed by the laws of the Republic of Türkiye. The courts and enforcement offices of the Republic of Türkiye have jurisdiction over any dispute that may arise.",
          "For consumers resident in the European Union, the protections afforded by the mandatory consumer-protection provisions of their country of residence and by their data-protection rights under the GDPR are reserved; this choice of law does not override those mandatory provisions.",
        ],
      },
    ],
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    description:
      "GDPR as the baseline standard; data processed, legal bases, EU hosting, international transfers (AI provider), and the KVKK Art. 9 cross-border transfer clause for users in Türkiye.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          `This Privacy Policy explains how personal data is processed within the Platform operated by ${OWNER_EN}. As its baseline data-protection standard, the policy follows the EU General Data Protection Regulation (GDPR / 2016/679); for users located in Türkiye, the Turkish Personal Data Protection Law No. 6698 (KVKK) — in particular Article 9 on transfers abroad — additionally applies. The KVKK Information Notice and the Explicit Consent Statement are integral parts of this policy.`,
        ],
      },
      {
        heading: "Data controller and hosting location",
        bullets: [
          "Data controller: PsyPractice. Data-subject requests are handled through the contact channel announced on the Platform (contact email: info@psycora.com).",
          "Hosting: the Platform and its database are hosted within the European Union — in a data center in the Netherlands (Oracle Cloud). For users located in the EU/EEA, data is processed and stored within the EU.",
          "The hosting provider acts solely as a processor, under an executed Data Processing Agreement (DPA).",
        ],
      },
      {
        heading: "Data collected",
        bullets: [
          "Account data: name, email, hashed password, subscription/plan information.",
          "Usage data: created sessions, session transcripts, generated patient cases, supervisor feedback reports.",
          "Technical data: session records, IP, browser/locale preference, rate-limit and security logs.",
          "Optional: the User's own Anthropic API key (stored encrypted).",
        ],
      },
      {
        heading: "Purposes of processing and legal basis (GDPR Art. 6)",
        bullets: [
          "Performance of a contract (Art. 6(1)(b)): account creation, running sessions, multi-session patient memory, supervisor evaluation, subscription and usage-limit management.",
          "Legitimate interest (Art. 6(1)(f)): security, abuse prevention, system improvement, and debugging.",
          "Legal obligation (Art. 6(1)(c)): retention/reporting arising from legislation.",
          "Consent (Art. 6(1)(a)): analytics/product-improvement processing not strictly necessary for the service and, where required, transfer outside the EU/EEA.",
        ],
      },
      {
        heading: "International transfers",
        paras: [
          "Because hosting is within the EU (Netherlands), the data of EU/EEA users is, as a rule, processed within the EU with no transfer to a third country at that layer. However, to generate AI responses, session content and prompts are transmitted to the AI service provider (Anthropic), whose processing infrastructure may be located outside the EU/EEA (e.g. the United States).",
          "Such transfers outside the EU/EEA rely on appropriate safeguards under GDPR Art. 44 et seq. — in particular the European Commission's Standard Contractual Clauses (SCCs) — and a data processing agreement (DPA) is concluded with the provider. The Platform does NOT sell personal data for marketing purposes; data is not shared with third parties except where strictly necessary to operate the service (lawful requests by competent authorities reserved).",
        ],
      },
      {
        heading: "For users in Türkiye — KVKK Art. 9 (transfer abroad)",
        paras: [
          "The personal data of users located in Türkiye is transferred outside Türkiye because hosting is in the Netherlands (EU), and may also be transferred to Anthropic for AI processing. These transfers fall under KVKK Art. 9 and rely on appropriate safeguards (the standard contract / undertaking published by the Board) or, where required, on your explicit consent. The scope, recipients, and purpose of the transfer are detailed in the KVKK Information Notice and the Explicit Consent Statement.",
        ],
      },
      {
        heading: "Data-subject rights (GDPR Art. 15–22)",
        bullets: [
          "Access, rectification, erasure ('right to be forgotten'), and restriction of processing.",
          "Data portability: receiving the data you provided in a structured, commonly used, machine-readable format.",
          "Objection: objecting to processing based on legitimate interest and to direct marketing.",
          "Rights relating to solely automated individual decision-making/profiling.",
          "Withdrawing consent at any time for consent-based processing (withdrawal does not affect the lawfulness of processing up to that point).",
          "The right to lodge a complaint with a supervisory authority: the relevant data protection authority in the EU; the Personal Data Protection Authority (KVKK) in Türkiye.",
        ],
      },
      {
        heading: "Retention and security",
        bullets: [
          "Data is retained for as long as the processing purpose requires and for the periods prescribed by applicable law; once the period expires it is deleted or anonymized.",
          "Sensitive fields (e.g. the User's API key, password) are kept encrypted/hashed; access is permission-based.",
          "As no method of transmission or storage is 100% secure, absolute security cannot be guaranteed.",
        ],
      },
      {
        heading: "Prohibition on real patient data",
        paras: [
          "The Platform is a simulation software; entering personal/health data belonging to real clients is prohibited. All liability arising from the User entering data in breach of this prohibition rests exclusively with the User.",
        ],
      },
    ],
  },
  {
    id: "kvkk",
    title: "KVKK Information Notice",
    description:
      "Data controller, processing purposes, legal basis, transfer, and data-subject rights under Article 10 of Law No. 6698.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "Data Controller",
        paras: [
          `Pursuant to Article 10 of the Turkish Personal Data Protection Law No. 6698 (“KVKK”), your personal data is processed by ${OWNER_EN} in its capacity as Data Controller, within the scope described below.`,
        ],
      },
      {
        heading: "Data processed and purposes",
        bullets: [
          "Identity/Contact (name, email): account creation, authentication, communication.",
          "Customer transaction (subscription, plan, usage): service delivery, billing, limit enforcement.",
          "Transaction security (session, IP, logs): security, abuse prevention, legal obligation.",
          "User content (session transcripts, generated cases): delivery of the software service and feedback generation.",
          "Institutional cohort membership (cohort identifier, membership period): where the user joins a cohort run by an institution, instructor visibility and administration of the cohort.",
        ],
      },
      {
        heading: "Legal basis (Art. 5)",
        bullets: [
          "Conclusion/performance of a contract: data necessary to provide the service.",
          "Legitimate interest of the data controller: security, fraud prevention, system improvement.",
          "Legal obligation: retention/reporting arising from legislation.",
          "Explicit consent: transfer abroad (Art. 9) and additional processing not strictly necessary for the service.",
        ],
      },
      {
        heading: "Transfer abroad (Art. 9)",
        paras: [
          "Because the Platform and its database are hosted within the European Union — in a data center in the Netherlands (Oracle Cloud) — the personal data of users located in Türkiye is transferred outside Türkiye (to the EU). In addition, to generate AI responses, session content and prompts may be transferred to the AI service provider (Anthropic), whose processing infrastructure may be located outside the EU/EEA (e.g. the United States).",
          "These transfers abroad fall under KVKK Art. 9 and rely on appropriate safeguards — such as the standard contract / undertaking published by the Board — or, where required, on your explicit consent. Data processing agreements (DPAs) are concluded with the hosting and service providers.",
        ],
      },
      {
        heading: "Institutional cohorts and instructor visibility",
        paras: [
          "If you join a cohort opened by a university, clinic, or similar institution, your session content (transcripts) and supervisor reports can be seen by that cohort's instructor for the duration of the cohort. This visibility covers only your sessions attached to the cohort and ends when your cohort membership ends.",
          "This access is obtained through a SEPARATE explicit-consent screen shown when you join the cohort; it is not part of the Platform's general acceptance. If you do not give that consent you cannot join the cohort, but you can carry on using the Platform individually exactly as before. Withdrawing the consent ends your cohort membership and leaves your individual use unaffected.",
        ],
      },
      {
        heading: "Data-subject rights (Art. 11)",
        bullets: [
          "To learn whether your personal data is processed and, if so, to request information.",
          "To learn the purpose of processing and whether it is used in accordance with that purpose.",
          "To know the third parties to whom data is transferred domestically or abroad.",
          "To request rectification of incomplete/incorrect processing and, where conditions are met, deletion/destruction.",
          "To object to a result against you arising solely from automated analysis.",
          "To claim compensation for damage suffered due to unlawful processing.",
        ],
      },
      {
        heading: "Applications",
        paras: [
          "You may submit requests concerning your rights through the contact channel announced by the Data Controller. Applications are concluded within the periods prescribed by the KVKK and its secondary legislation.",
        ],
      },
    ],
  },
  {
    id: "consent",
    title: "Explicit Consent Statement",
    description:
      "Separate from the information notice; consent freely given to transfer abroad and AI processing.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          "I declare that I have read and understood the KVKK Information Notice, and that I freely, in an informed and explicit manner, consent to the processing described below. (Pursuant to KVKK Board decision No. 2018/90, explicit consent is obtained separately from the duty to inform.)",
        ],
      },
      {
        heading: "Processing consented to",
        bullets: [
          "Transfer of my personal data outside Türkiye, and its storage and processing there, because the Platform is hosted in the European Union (Netherlands / Oracle Cloud) (KVKK Art. 9).",
          "Transfer and processing of my session transcripts and prompts at the AI service provider (Anthropic), whose processing infrastructure may be located outside the EU/EEA, for the purpose of generating AI responses (KVKK Art. 9).",
          "Processing for product improvement and analytics that is not strictly necessary to provide the service.",
          "Receiving service-related notifications.",
        ],
      },
      {
        heading: "Withdrawal of consent",
        paras: [
          "I understand that I may withdraw my explicit consent at any time; however, since transfer abroad to the AI provider is technically required for the service to function, I accept that withdrawing this consent may result in my being unable to use the relevant features or the service as a whole. Withdrawal does not affect the lawfulness of processing carried out up to the date of withdrawal.",
        ],
      },
    ],
  },
  {
    // Counterpart of the TR "outreach" notice — see the comment there.
    id: "outreach",
    title: "Outreach Communication Notice",
    description:
      "For academics and institutional contacts who received a corporate outreach email: where the data came from, why it is processed, the legal basis, and how to opt out.",
    updated: "2026-08-06",
    sections: [
      {
        paras: [
          "This notice informs recipients of corporate outreach emails sent by PsyPractice to university psychology departments and their academic staff, in accordance with Article 10 of the Turkish Personal Data Protection Law No. 6698 (KVKK) and the Communiqué on the Procedures and Principles for Fulfilling the Disclosure Obligation. Because your personal data was not obtained directly from you, the disclosure is made at the latest at the time of first contact, which is why the outreach email links here.",
        ],
      },
      {
        heading: "1. Data controller",
        bullets: [
          `Name: ${MERCHANT.legalNameEn} (${MERCHANT.typeEn})`,
          `Address: ${MERCHANT.addressEn}`,
          `Tax office / no: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}`,
          `Email: ${MERCHANT.email}`,
          `Web: ${MERCHANT.website}`,
        ],
      },
      {
        heading: "2. Data processed and its source",
        paras: [
          "Processing is limited to professional contact data: name, academic title, university and department, institutional email address and, where published, institutional phone number.",
          "This data was not collected from you but compiled from the publicly published official department and faculty pages of universities. No special categories of data are processed, and private (non-institutional) email addresses are not used for outreach.",
        ],
      },
      {
        heading: "3. Purpose",
        paras: [
          "Your data is processed to introduce a software tool built for psychology education, to invite your department to a possible pilot or cooperation conversation, and to carry out the correspondence relating to it. It is not used for any other purpose, not sold, and not transferred to third parties for marketing.",
        ],
      },
      {
        heading: "4. Legal basis",
        paras: [
          "Processing relies on the legitimate interests of the data controller under Article 5/2-(f) KVKK, provided this does not harm your fundamental rights and freedoms: contact is made only to an institutional address, only on a matter directly related to the recipient's professional field, and a limited number of times.",
          "The mere fact that data is publicly available online is not by itself a legal basis. The Personal Data Protection Board has held that data being public does not make it usable for every purpose, and that the intent and purpose of making it public must be assessed separately; Article 5/2-(d) is therefore not relied upon.",
        ],
      },
      {
        heading: "5. Transfers and retention",
        paras: [
          "Correspondence is carried out through an email provider whose infrastructure may be located abroad; such transfers are made with appropriate safeguards under Article 9 KVKK.",
          "Your data is deleted once the purpose of the contact ends or upon your request. After a deletion request, a strictly limited 'do not contact' record is kept solely to make sure you are not contacted again; it is never used for outreach.",
        ],
      },
      {
        heading: "6. Commercial electronic messages and the right to refuse",
        paras: [
          "Promotional messages fall within the scope of Law No. 6563 on the Regulation of Electronic Commerce. You may refuse to receive them without giving any reason; refusals are processed within three business days at the latest, after which no further promotional message is sent. Replying to the email you received, or writing to " + MERCHANT.email + ", is sufficient.",
        ],
      },
      {
        heading: "7. Your rights",
        paras: [
          "Under Article 11 KVKK you have the right to learn whether your personal data is processed, to request information about it, to learn the purpose of processing and whether it is used accordingly, to know the third parties to whom it is transferred in Turkey or abroad, to request correction of incomplete or inaccurate data, to request erasure or destruction, to request that these actions be notified to third parties, to object to a result against you produced solely by automated analysis, and to claim compensation for damage suffered.",
          `Requests may be sent to ${MERCHANT.email} and are answered within thirty days at the latest. You also retain the right to lodge a complaint with the Personal Data Protection Board.`,
        ],
      },
    ],
  },
  {
    id: "cookies",
    title: "Cookie Policy",
    description: "Types of cookies used, their purposes, and preference management.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          "The Platform uses cookies and similar local-storage technologies (localStorage) to maintain your session and remember your preferences.",
        ],
      },
      {
        heading: "Technologies used",
        bullets: [
          "Strictly necessary: session/authentication token, security. The service does not work without these.",
          "Functional: language/locale preference (NEXT_LOCALE), interface preferences (e.g. theme).",
          "Performance/security: technical logs for rate-limiting and abuse detection.",
        ],
      },
      {
        heading: "Preference management",
        paras: [
          "You can delete or block cookies in your browser; however, blocking strictly necessary cookies will prevent sign-in and core functionality. In line with GDPR / ePrivacy principles and the KVKK Cookie Guide, non-essential cookies are used only on the basis of your explicit preference/consent.",
        ],
      },
    ],
  },
  {
    id: "about",
    title: "About Us",
    description:
      "PsyPractice's purpose, the service it offers, and merchant (seller) contact details.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          "PsyPractice is an AI-assisted patient simulation and clinical-skills platform built for psychology students and newly graduated, early-career clinicians. Users conduct text-based therapy sessions; at the end of each session a supervisor AI evaluates the conversation and produces a developmental feedback report. The patient AI carries the therapeutic relationship forward session by session through cross-session memory.",
        ],
      },
      {
        heading: "Our mission",
        paras: [
          "Our aim is to give future mental-health professionals a safe, repeatable environment in which to practice interviewing skills without any risk of harming real clients. PsyPractice is not an education or teaching service; it is a software tool that lets the user carry out and record their own practice. It is not real therapy or counselling, it is not a substitute for clinical supervision, it is not a diagnostic or assessment instrument, and it must not be used in crises or emergencies.",
        ],
      },
      {
        heading: "What we offer",
        bullets: [
          "Text-based therapy session simulation across diverse patient profiles synthesized from clinical literature.",
          "Automated supervisor evaluation reports after each session.",
          "A 'living patient' model with cross-session memory that develops the therapeutic relationship over time.",
          "Custom patient generation, letting students create their own AI cases.",
          "Progress metrics that track microskill development.",
        ],
      },
      {
        heading: "Seller / Business Information",
        bullets: [
          `Trade name: ${MERCHANT.legalNameEn} (${MERCHANT.typeEn})`,
          `Address: ${MERCHANT.addressEn}`,
          `Tax office / no: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}`,
          `Email: ${MERCHANT.email}`,
          `Web: ${MERCHANT.website}`,
        ],
      },
    ],
  },
  {
    id: "distance-sales",
    title: "Distance Sales Agreement",
    description:
      "Seller, buyer, service, price, payment, performance, and right-of-withdrawal terms under Turkish Law No. 6502 and the Distance Contracts Regulation.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "1. Parties",
        bullets: [
          `SELLER: ${MERCHANT.legalNameEn} (${MERCHANT.typeEn}). Address: ${MERCHANT.addressEn}. Tax office/no: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}. Email: ${MERCHANT.email}.`,
          "BUYER: the natural or legal person who creates an account on the Platform and purchases a paid subscription (the \"Buyer\").",
        ],
      },
      {
        heading: "2. Subject of the Agreement",
        paras: [
          "The subject of this Agreement is to determine the rights and obligations of the parties, in accordance with Turkish Law No. 6502 on Consumer Protection and the Distance Contracts Regulation, regarding the sale and performance of the digital service subscription purchased electronically by the Buyer through the Platform, the characteristics and sale price of which are stated below.",
        ],
      },
      {
        heading: "3. Nature of the Service and Price",
        bullets: [
          "The service sold is the AI-assisted patient-simulation features offered under a paid subscription plan of the PsyPractice software (a digital service / right to use the software).",
          "PsyPractice is not an education or teaching service; it is a software tool that lets the user carry out and record their own practice. The subscription fee is consideration for access to that software; it is not a fee for lessons, curriculum, examinations, consultancy, or any document.",
          "The plan content, term (monthly/annual) and current price are shown clearly on the Platform's pricing/checkout screen at the moment of purchase; the total amount including taxes is disclosed to the Buyer before payment.",
          "Prices apply in the currency displayed on the Platform; unless cancelled, the subscription renews automatically at the same price at the end of the selected term.",
        ],
      },
      {
        heading: "4. Payment",
        paras: [
          "Payments are collected securely by credit/debit card through the licensed payment institution iyzico. Card details are not stored by the Seller; the payment is processed through iyzico's secure systems. For recurring (subscription) payments, the amount is automatically charged each renewal period to the card approved by the Buyer.",
        ],
      },
      {
        heading: "5. Performance and Delivery",
        paras: [
          "The service is digital and involves no physical delivery. Upon confirmation of payment, the subscription features are made available in the Buyer's account immediately. Performance takes place electronically by providing uninterrupted access throughout the subscription term.",
        ],
      },
      {
        heading: "6. Right of Withdrawal and Its Exception",
        bullets: [
          "Pursuant to Article 15/1-(ğ) of the Distance Contracts Regulation, the right of withdrawal cannot be exercised for intangible (digital) goods/services performed instantly in electronic form where performance has begun with the Buyer's consent.",
          "The Buyer acknowledges at the time of purchase that the service will be performed immediately and that, therefore, no right of withdrawal exists. The Buyer may cancel the subscription at any time to stop renewal; cancellation takes effect at the end of the current paid period.",
          "For detailed refund terms, see \"Delivery & Refund Terms\".",
        ],
      },
      {
        heading: "7. Disputes and Entry into Force",
        paras: [
          "In case of a dispute, the Buyer may apply to the Consumer Arbitration Committee or the Consumer Courts at their place of residence within the monetary limits announced by the Ministry of Trade. This Agreement is concluded and enters into force upon the Buyer's approval in electronic form.",
        ],
      },
    ],
  },
  {
    id: "delivery-refund",
    title: "Delivery & Refund Terms",
    description:
      "Delivery of the digital service, subscription cancellation, the withdrawal exception, and refund conditions.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "Delivery (Digital Service)",
        bullets: [
          "PsyPractice is an entirely digital service; there is no physical product shipment or courier delivery.",
          "The moment payment is confirmed, subscription features are activated automatically and instantly in the Buyer's account; there is no separate delivery time or delivery fee.",
          "The service is accessed via the web browser of any internet-connected device.",
        ],
      },
      {
        heading: "Subscription Cancellation",
        bullets: [
          "The Buyer may cancel the subscription at any time from account settings. Cancellation takes effect at the end of the current paid period and stops the next automatic renewal.",
          "After cancellation, access to the service continues for the remainder of the paid period.",
        ],
      },
      {
        heading: "Right of Withdrawal and Refund",
        bullets: [
          "Because the service is a digital service performed immediately with the Buyer's consent, as a rule no right of withdrawal — and thus no refund right — arises under Article 15/1-(ğ) of the Distance Contracts Regulation.",
          "In cases of clear defect such as an erroneous/duplicate charge, complete inability to access the service for technical reasons, or similar, the Buyer may request a refund by contacting the Seller within a reasonable time from the payment date.",
          `Refund requests are sent to ${MERCHANT.email}; approved refunds are returned to the card used for payment through the iyzico infrastructure, as a rule within 14 days. The refund period may vary depending on the bank's processing time.`,
        ],
      },
      {
        heading: "Contact",
        paras: [
          `For all delivery, cancellation and refund matters: ${MERCHANT.email}${MERCHANT.phone ? " · " + MERCHANT.phone : ""}.`,
        ],
      },
    ],
  },
];

// ── Русский ──────────────────────────────────────────────────────────────────
// TR/EN belgelerinin BİREBİR karşılığıdır: bölüm sırası, başlık numaraları ve
// hukuki iddialar aynıdır. Esasa ilişkin bir değişiklik üç dilde birlikte yapılır
// ve LEGAL_VERSION birlikte artar — aksi halde kullanıcının onayladığı sürüm ile
// okuduğu metin ayrışır.
const DOCS_RU: LegalDoc[] = [
  {
    id: "terms",
    title: "Условия использования и отказ от ответственности",
    description:
      "Характер и границы услуги, что она не заменяет, обязательства пользователя и полный отказ от ответственности, возлагающий её на пользователя.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "1. Стороны и принятие",
        paras: [
          `Настоящие Условия использования заключаются между ${OWNER_RU} и любым физическим или юридическим лицом, которое получает доступ к Платформе или использует её (далее — «Пользователь»). Создавая учётную запись, выполняя вход или используя Платформу любым способом, Пользователь подтверждает, что прочитал, понял и безоговорочно принимает настоящие Условия в полном объёме и обязуется их соблюдать. Тот, кто не принимает настоящие Условия, не должен пользоваться Платформой.`,
        ],
      },
      {
        heading: "2. Характер и границы услуги",
        bullets: [
          "PsyPractice — это программное обеспечение для СИМУЛЯЦИИ пациента с использованием искусственного интеллекта, которое позволяет студентам-психологам и начинающим клиницистам вести и фиксировать собственную практику клинической беседы в безопасной среде.",
          "PsyPractice не является образовательной или преподавательской услугой; это программный инструмент, который позволяет пользователю вести и фиксировать собственную практику. Платформа не предоставляет занятий, учебных программ, экзаменов или документов и не присваивает никаких званий, квалификаций или полномочий.",
          "Смоделированный «пациент» не является реальным клиентом; его ответы синтезированы искусственным интеллектом, могут быть ошибочными, неполными, противоречивыми или вводящими в заблуждение и не претендуют на клиническую точность или достоверность.",
        ],
      },
      {
        heading: "2.1. Что Платформа не заменяет",
        bullets: [
          "Она НЕ является реальной терапией или консультированием и не заменяет ни одной сессии, проведённой с реальным клиентом.",
          "Она НЕ заменяет клиническую супервизию. Отчёты обратной связи, создаваемые ИИ-супервизором, — это автоматически сгенерированный текст; они не являются экспертным заключением, официальной оценкой или отметкой.",
          "Она НЕ является диагностическим, скрининговым или оценочным инструментом; никакой результат работы Платформы не может использоваться для постановки диагноза или принятия клинических решений в отношении реального человека.",
          "Её НЕЛЬЗЯ использовать в кризисных или неотложных ситуациях. При суицидальных мыслях, риске причинения вреда себе или другим либо в любой сопоставимой чрезвычайной ситуации закройте Платформу и немедленно обратитесь в местную экстренную службу или компетентное медицинское учреждение.",
          "Она НЕ является источником профессиональной компетентности, лицензии, звания или полномочий; время, проведённое на Платформе, и её результаты не могут предъявляться какой-либо организации в качестве подтверждения квалификации.",
        ],
      },
      {
        heading: "3. Заявления и обязательства пользователя",
        bullets: [
          "Пользователь обязуется использовать Платформу исключительно в законных и этических рамках, в целях развития собственной профессиональной практики.",
          "Пользователь обязуется НЕ вводить какие-либо РЕАЛЬНЫЕ данные пациента или клиента, идентифицирующие сведения либо данные о здоровье, относящиеся к особым категориям и принадлежащие какому-либо лицу; все правовые последствия таких действий несёт исключительно Пользователь.",
          "Пользователь обязуется не использовать полученные от Платформы результаты для диагностики, лечения, вмешательства или принятия клинических решений в отношении реального человека.",
          "Пользователь единолично отвечает за собственную профессиональную компетентность, объём своих полномочий и соблюдение применимых профессиональных и этических норм.",
          "Безопасность учётной записи, конфиденциальность пароля и любые действия, совершённые под учётной записью Пользователя, находятся под ответственностью Пользователя.",
        ],
      },
      {
        heading: "4. Вся ответственность лежит на пользователе (отказ от требований)",
        paras: [
          "Платформа предоставляется «КАК ЕСТЬ» и «ПО МЕРЕ ДОСТУПНОСТИ», без каких-либо прямых или подразумеваемых гарантий (включая бесперебойную работу, отсутствие ошибок, пригодность для конкретной цели, точность или актуальность).",
          "Пользователь принимает и обязуется, что несёт ИСКЛЮЧИТЕЛЬНУЮ и ЛИЧНУЮ ответственность за любые прямые, косвенные, случайные, специальные или последующие убытки (включая имущественный и моральный вред, ущерб профессиональной репутации, утрату данных, упущенную выгоду и вред, причинённый третьим лицам), возникшие вследствие использования или невозможности использования Платформы, доверия к результатам работы ИИ либо применения таких результатов любым способом.",
          "В максимальной степени, допускаемой применимым правом, Пользователь настоящим безотзывно и безоговорочно ОТКАЗЫВАЕТСЯ — сейчас и заранее — от любых претензий, исков и прав на возмещение, которые он мог бы предъявить Платформе, её владельцам, работникам, разработчикам, деловым партнёрам и поставщикам услуг в связи с любым вредом, с которым он может столкнуться при неблагоприятном исходе.",
          "Пользователь также обязуется полностью оградить Платформу от ответственности (возместить ей) в отношении любых требований, убытков, штрафов и расходов (включая гонорары адвокатов), если к Платформе обратится третье лицо или организация по причине вины, небрежности Пользователя либо использования им Платформы с нарушением настоящих Условий.",
        ],
      },
      {
        heading: "5. Ограничение ответственности",
        paras: [
          "Без ущерба для императивных норм применимого права совокупная юридическая ответственность Платформы в любом случае ограничивается суммой, фактически уплаченной Пользователем Платформе за 12 месяцев, предшествующих событию, послужившему основанием соответствующего требования. При бесплатном использовании эта сумма равна нулю.",
        ],
      },
      {
        heading: "6. Интеллектуальная собственность, изменение и прекращение",
        bullets: [
          "Все права интеллектуальной собственности на Платформу, её программное обеспечение, дизайн, шаблоны случаев пациентов и содержание принадлежат Платформе.",
          "Все передаваемые имущественные права интеллектуальной собственности на содержание, создаваемое Пользователем на Платформе (включая написанные им реплики терапевта, стенограммы сессий и производные от них материалы), считаются переданными Платформе исключительно, бессрочно и безвозмездно в момент создания такого содержания; Платформа вправе использовать, воспроизводить и обрабатывать это содержание по всему миру и без ограничений в любых целях, включая оказание услуги, формирование обратной связи, а также улучшение и развитие системы (в том числе улучшение методами машинного обучения). Права Пользователя в отношении персональных данных согласно применимому законодательству о защите данных (GDPR / KVKK) настоящей передачей не затрагиваются и сохраняются.",
          "Платформа оставляет за собой право в любое время и без предварительного уведомления изменять, приостанавливать или прекращать действие настоящих Условий и объём услуги.",
          "В случае нарушения настоящих Условий Платформа вправе без предварительного уведомления приостановить или закрыть учётную запись.",
        ],
      },
      {
        heading: "7. Применимое право и подсудность",
        paras: [
          "Настоящие Условия регулируются правом Турецкой Республики. Споры, которые могут возникнуть, подсудны судам и органам принудительного исполнения Турецкой Республики.",
          "Для потребителей, проживающих в Европейском союзе, сохраняются гарантии, предоставляемые императивными нормами законодательства о защите прав потребителей их страны проживания, а также их права в области защиты данных по GDPR; настоящий выбор права не отменяет эти императивные нормы.",
        ],
      },
    ],
  },
  {
    id: "privacy",
    title: "Политика конфиденциальности",
    description:
      "GDPR как базовый стандарт; обрабатываемые данные, правовые основания, хостинг в ЕС, международные передачи (поставщик ИИ) и оговорка о трансграничной передаче по ст. 9 KVKK для пользователей в Турции.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          `Настоящая Политика конфиденциальности объясняет, как обрабатываются персональные данные на Платформе, управляемой ${OWNER_RU}. В качестве базового стандарта защиты данных Политика следует Общему регламенту ЕС о защите данных (GDPR / 2016/679); для пользователей, находящихся в Турции, дополнительно применяется Закон Турции о защите персональных данных № 6698 (KVKK), в частности его статья 9 о передаче за рубеж. Уведомление по KVKK и Заявление о явном согласии являются неотъемлемой частью настоящей Политики.`,
        ],
      },
      {
        heading: "Оператор данных и место размещения",
        bullets: [
          "Оператор данных: PsyPractice. Обращения субъектов данных обрабатываются по каналу связи, указанному на Платформе (адрес для связи: info@psycora.com).",
          "Хостинг: Платформа и её база данных размещены в пределах Европейского союза — в дата-центре в Нидерландах (Oracle Cloud). Для пользователей, находящихся в ЕС/ЕЭЗ, данные обрабатываются и хранятся внутри ЕС.",
          "Поставщик хостинга выступает исключительно как обработчик на основании заключённого соглашения об обработке данных (DPA).",
        ],
      },
      {
        heading: "Собираемые данные",
        bullets: [
          "Данные учётной записи: имя, адрес электронной почты, хешированный пароль, сведения о подписке и тарифе.",
          "Данные об использовании: созданные сессии, стенограммы сессий, сгенерированные случаи пациентов, отчёты обратной связи супервизора.",
          "Технические данные: записи о сеансах, IP-адрес, языковые настройки и настройки браузера, журналы ограничения частоты запросов и безопасности.",
          "Необязательно: собственный ключ API Anthropic Пользователя (хранится в зашифрованном виде).",
        ],
      },
      {
        heading: "Цели обработки и правовое основание (ст. 6 GDPR)",
        bullets: [
          "Исполнение договора (ст. 6(1)(b)): создание учётной записи, проведение сессий, сквозная память пациента, разбор супервизором, управление подпиской и лимитами использования.",
          "Законный интерес (ст. 6(1)(f)): безопасность, предотвращение злоупотреблений, улучшение системы и устранение ошибок.",
          "Правовая обязанность (ст. 6(1)(c)): хранение и отчётность, вытекающие из законодательства.",
          "Согласие (ст. 6(1)(a)): аналитика и обработка для улучшения продукта, не являющиеся строго необходимыми для оказания услуги, а также, где это требуется, передача за пределы ЕС/ЕЭЗ.",
        ],
      },
      {
        heading: "Международные передачи",
        paras: [
          "Поскольку хостинг находится в ЕС (Нидерланды), данные пользователей из ЕС/ЕЭЗ, как правило, обрабатываются внутри ЕС и на этом уровне не передаются в третьи страны. Однако для генерации ответов ИИ содержание сессий и запросы передаются поставщику ИИ-услуг (Anthropic), чья обрабатывающая инфраструктура может находиться за пределами ЕС/ЕЭЗ (например, в США).",
          "Такие передачи за пределы ЕС/ЕЭЗ опираются на надлежащие гарантии по ст. 44 и последующим статьям GDPR — в частности на Стандартные договорные условия (SCC) Европейской комиссии, — и с поставщиком заключено соглашение об обработке данных (DPA). Платформа НЕ продаёт персональные данные в маркетинговых целях; данные не передаются третьим лицам, кроме случаев, строго необходимых для оказания услуги (за исключением законных запросов компетентных органов).",
        ],
      },
      {
        heading: "Для пользователей в Турции — ст. 9 KVKK (передача за рубеж)",
        paras: [
          "Персональные данные пользователей, находящихся в Турции, передаются за пределы Турции, поскольку хостинг расположен в Нидерландах (ЕС), а также могут передаваться компании Anthropic для обработки средствами ИИ. Эти передачи подпадают под ст. 9 KVKK и опираются на надлежащие гарантии (типовой договор или обязательство, опубликованные Советом) либо, где это требуется, на ваше явное согласие. Объём, получатели и цель передачи подробно описаны в Уведомлении по KVKK и Заявлении о явном согласии.",
        ],
      },
      {
        heading: "Права субъекта данных (ст. 15–22 GDPR)",
        bullets: [
          "Доступ, исправление, удаление («право быть забытым») и ограничение обработки.",
          "Переносимость данных: получение предоставленных вами данных в структурированном, широко используемом, машиночитаемом формате.",
          "Возражение: против обработки на основании законного интереса и против прямого маркетинга.",
          "Права, связанные с принятием решений исключительно автоматизированным способом и профилированием.",
          "Отзыв согласия в любой момент для обработки на основании согласия (отзыв не влияет на законность обработки до этого момента).",
          "Право подать жалобу в надзорный орган: соответствующий орган по защите данных в ЕС; Управление по защите персональных данных (KVKK) в Турции.",
        ],
      },
      {
        heading: "Хранение и безопасность",
        bullets: [
          "Данные хранятся столько, сколько требует цель обработки, и в течение сроков, предписанных применимым законодательством; по истечении срока они удаляются или обезличиваются.",
          "Чувствительные поля (например, ключ API Пользователя, пароль) хранятся в зашифрованном или хешированном виде; доступ к ним предоставляется по правам.",
          "Поскольку ни один способ передачи или хранения не является на 100 % безопасным, абсолютная безопасность не может быть гарантирована.",
        ],
      },
      {
        heading: "Запрет на данные реальных пациентов",
        paras: [
          "Платформа является программой-симулятором; вводить персональные данные и данные о здоровье реальных клиентов запрещено. Всю ответственность за ввод данных в нарушение этого запрета несёт исключительно Пользователь.",
        ],
      },
    ],
  },
  {
    id: "kvkk",
    title: "Уведомление по KVKK",
    description:
      "Оператор данных, цели обработки, правовое основание, передача и права субъекта данных согласно статье 10 Закона № 6698.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "Оператор данных",
        paras: [
          `В соответствии со статьёй 10 Закона Турции о защите персональных данных № 6698 («KVKK») ваши персональные данные обрабатываются ${OWNER_RU} в качестве Оператора данных в описанном ниже объёме.`,
        ],
      },
      {
        heading: "Обрабатываемые данные и цели",
        bullets: [
          "Идентификация и контакты (имя, электронная почта): создание учётной записи, аутентификация, связь.",
          "Клиентские операции (подписка, тариф, использование): оказание услуги, выставление счетов, соблюдение лимитов.",
          "Безопасность операций (сеансы, IP, журналы): безопасность, предотвращение злоупотреблений, правовая обязанность.",
          "Содержание, созданное пользователем (стенограммы сессий, сгенерированные случаи): оказание программной услуги и формирование обратной связи.",
          "Членство в институциональной группе (идентификатор группы, период членства): при вступлении пользователя в группу, организованную учреждением, — видимость для преподавателя и администрирование группы.",
        ],
      },
      {
        heading: "Правовое основание (ст. 5)",
        bullets: [
          "Заключение и исполнение договора: данные, необходимые для оказания услуги.",
          "Законный интерес оператора данных: безопасность, предотвращение мошенничества, улучшение системы.",
          "Правовая обязанность: хранение и отчётность, вытекающие из законодательства.",
          "Явное согласие: передача за рубеж (ст. 9) и дополнительная обработка, не являющаяся строго необходимой для оказания услуги.",
        ],
      },
      {
        heading: "Передача за рубеж (ст. 9)",
        paras: [
          "Поскольку Платформа и её база данных размещены в пределах Европейского союза — в дата-центре в Нидерландах (Oracle Cloud), — персональные данные пользователей, находящихся в Турции, передаются за пределы Турции (в ЕС). Кроме того, для генерации ответов ИИ содержание сессий и запросы могут передаваться поставщику ИИ-услуг (Anthropic), чья обрабатывающая инфраструктура может находиться за пределами ЕС/ЕЭЗ (например, в США).",
          "Эти передачи за рубеж подпадают под ст. 9 KVKK и опираются на надлежащие гарантии — такие как типовой договор или обязательство, опубликованные Советом, — либо, где это требуется, на ваше явное согласие. С поставщиками хостинга и услуг заключены соглашения об обработке данных (DPA).",
        ],
      },
      {
        heading: "Институциональные группы и видимость для преподавателя",
        paras: [
          "Если вы вступаете в группу, открытую университетом, клиникой или подобным учреждением, содержание ваших сессий (стенограммы) и отчёты супервизора могут быть доступны преподавателю этой группы в течение всего срока её работы. Эта видимость охватывает только ваши сессии, относящиеся к группе, и прекращается по окончании вашего членства в ней.",
          "Такой доступ предоставляется через ОТДЕЛЬНЫЙ экран явного согласия, показываемый при вступлении в группу; он не входит в общее принятие условий Платформы. Если вы не дадите это согласие, вы не сможете вступить в группу, но сможете продолжать пользоваться Платформой индивидуально ровно так же, как прежде. Отзыв согласия прекращает ваше членство в группе и не затрагивает индивидуальное использование.",
        ],
      },
      {
        heading: "Права субъекта данных (ст. 11)",
        bullets: [
          "Узнать, обрабатываются ли ваши персональные данные, и, если да, запросить информацию.",
          "Узнать цель обработки и используются ли данные в соответствии с этой целью.",
          "Знать третьих лиц, которым данные передаются внутри страны или за рубеж.",
          "Требовать исправления неполной или неверной обработки и, при соблюдении условий, удаления или уничтожения данных.",
          "Возражать против неблагоприятного для вас результата, возникшего исключительно вследствие автоматизированного анализа.",
          "Требовать возмещения вреда, причинённого неправомерной обработкой.",
        ],
      },
      {
        heading: "Обращения",
        paras: [
          "Обращения по поводу ваших прав можно направлять по каналу связи, объявленному Оператором данных. Обращения рассматриваются в сроки, предписанные KVKK и подзаконными актами к нему.",
        ],
      },
    ],
  },
  {
    id: "consent",
    title: "Заявление о явном согласии",
    description:
      "Отдельно от уведомления; свободно данное согласие на передачу за рубеж и обработку средствами ИИ.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          "Я заявляю, что прочитал(а) и понял(а) Уведомление по KVKK и что свободно, осознанно и явно даю согласие на описанную ниже обработку. (Согласно решению Совета по защите персональных данных № 2018/90 явное согласие получается отдельно от исполнения обязанности по информированию.)",
        ],
      },
      {
        heading: "Обработка, на которую даётся согласие",
        bullets: [
          "Передача моих персональных данных за пределы Турции, а также их хранение и обработка там, поскольку Платформа размещена в Европейском союзе (Нидерланды / Oracle Cloud) (ст. 9 KVKK).",
          "Передача и обработка стенограмм моих сессий и запросов у поставщика ИИ-услуг (Anthropic), чья обрабатывающая инфраструктура может находиться за пределами ЕС/ЕЭЗ, в целях генерации ответов ИИ (ст. 9 KVKK).",
          "Обработка в целях улучшения продукта и аналитики, не являющаяся строго необходимой для оказания услуги.",
          "Получение уведомлений, связанных с услугой.",
        ],
      },
      {
        heading: "Отзыв согласия",
        paras: [
          "Я понимаю, что могу отозвать своё явное согласие в любой момент; однако, поскольку передача за рубеж поставщику ИИ технически необходима для работы услуги, я принимаю, что отзыв этого согласия может привести к невозможности пользоваться соответствующими функциями или услугой в целом. Отзыв не влияет на законность обработки, осуществлённой до даты отзыва.",
        ],
      },
    ],
  },
  {
    // TR/EN "outreach" bildiriminin karşılığı — oradaki yoruma bakınız.
    id: "outreach",
    title: "Уведомление о рассылке обращений",
    description:
      "Для преподавателей и институциональных контактов, получивших корпоративное письмо-обращение: откуда взяты данные, зачем они обрабатываются, правовое основание и как отказаться.",
    updated: "2026-08-06",
    sections: [
      {
        paras: [
          "Настоящее уведомление адресовано получателям корпоративных писем-обращений, которые PsyPractice направляет кафедрам психологии университетов и их преподавателям, и составлено в соответствии со статьёй 10 Закона Турции о защите персональных данных № 6698 (KVKK) и Коммюнике о порядке и принципах исполнения обязанности по информированию. Поскольку ваши персональные данные получены не непосредственно от вас, информирование осуществляется не позднее момента первого обращения — именно поэтому письмо содержит ссылку сюда.",
        ],
      },
      {
        heading: "1. Оператор данных",
        bullets: [
          `Наименование: ${MERCHANT.legalNameEn} (${MERCHANT.typeRu})`,
          `Адрес: ${MERCHANT.addressEn}`,
          `Налоговая инспекция / ИНН: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}`,
          `Электронная почта: ${MERCHANT.email}`,
          `Сайт: ${MERCHANT.website}`,
        ],
      },
      {
        heading: "2. Обрабатываемые данные и их источник",
        paras: [
          "Обработка ограничивается профессиональными контактными данными: имя, учёное звание, университет и кафедра, институциональный адрес электронной почты и, если он опубликован, институциональный номер телефона.",
          "Эти данные собраны не у вас, а составлены по общедоступным официальным страницам кафедр и факультетов университетов. Данные особых категорий не обрабатываются, а частные (не институциональные) адреса электронной почты для обращений не используются.",
        ],
      },
      {
        heading: "3. Цель",
        paras: [
          "Ваши данные обрабатываются, чтобы представить программный инструмент, созданный для подготовки психологов, пригласить вашу кафедру к разговору о возможном пилоте или сотрудничестве и вести связанную с этим переписку. Они не используются ни для каких иных целей, не продаются и не передаются третьим лицам для маркетинга.",
        ],
      },
      {
        heading: "4. Правовое основание",
        paras: [
          "Обработка опирается на законные интересы оператора данных согласно статье 5/2-(f) KVKK при условии, что это не наносит ущерба вашим основным правам и свободам: обращение направляется только на институциональный адрес, только по вопросу, непосредственно связанному с профессиональной сферой получателя, и ограниченное число раз.",
          "Сам по себе факт общедоступности данных в интернете не является правовым основанием. Совет по защите персональных данных указал, что общедоступность данных не делает их пригодными для любой цели и что намерение и цель их обнародования должны оцениваться отдельно; поэтому статья 5/2-(d) не используется в качестве основания.",
        ],
      },
      {
        heading: "5. Передача и хранение",
        paras: [
          "Переписка ведётся через поставщика электронной почты, инфраструктура которого может находиться за рубежом; такие передачи осуществляются с надлежащими гарантиями согласно статье 9 KVKK.",
          "Ваши данные удаляются по достижении цели обращения или по вашему запросу. После запроса на удаление сохраняется строго ограниченная запись «не связываться», единственное назначение которой — гарантировать, что к вам больше не обратятся; для рассылок она никогда не используется.",
        ],
      },
      {
        heading: "6. Коммерческие электронные сообщения и право на отказ",
        paras: [
          "Рекламные сообщения подпадают под действие Закона № 6563 о регулировании электронной торговли. Вы можете отказаться от их получения без объяснения причин; отказы обрабатываются не позднее трёх рабочих дней, после чего рекламные сообщения больше не направляются. Достаточно ответить на полученное письмо или написать на " + MERCHANT.email + ".",
        ],
      },
      {
        heading: "7. Ваши права",
        paras: [
          "Согласно статье 11 KVKK вы вправе узнать, обрабатываются ли ваши персональные данные, запросить о них информацию, узнать цель обработки и используются ли они в соответствии с ней, знать третьих лиц, которым они передаются в Турции или за рубежом, требовать исправления неполных или неточных данных, требовать их удаления или уничтожения, требовать уведомления об этих действиях третьих лиц, возражать против неблагоприятного для вас результата, полученного исключительно автоматизированным анализом, и требовать возмещения причинённого вреда.",
          `Запросы можно направлять на ${MERCHANT.email}; ответ даётся не позднее тридцати дней. За вами также сохраняется право подать жалобу в Совет по защите персональных данных.`,
        ],
      },
    ],
  },
  {
    id: "cookies",
    title: "Политика в отношении файлов cookie",
    description: "Виды используемых файлов cookie, их назначение и управление настройками.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          "Платформа использует файлы cookie и аналогичные технологии локального хранения (localStorage), чтобы поддерживать ваш сеанс и запоминать ваши настройки.",
        ],
      },
      {
        heading: "Используемые технологии",
        bullets: [
          "Строго необходимые: токен сеанса и аутентификации, безопасность. Без них услуга не работает.",
          "Функциональные: настройка языка и локали (NEXT_LOCALE), настройки интерфейса (например, тема).",
          "Производительность и безопасность: технические журналы для ограничения частоты запросов и выявления злоупотреблений.",
        ],
      },
      {
        heading: "Управление настройками",
        paras: [
          "Вы можете удалить или заблокировать файлы cookie в браузере; однако блокировка строго необходимых файлов cookie сделает невозможным вход и работу основных функций. В соответствии с принципами GDPR / ePrivacy и Руководством KVKK по файлам cookie неосновные файлы cookie используются только на основании вашего явного выбора или согласия.",
        ],
      },
    ],
  },
  {
    id: "about",
    title: "О нас",
    description:
      "Назначение PsyPractice, предоставляемая услуга и контактные данные продавца.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          "PsyPractice — это платформа симуляции пациента и отработки клинических навыков на базе искусственного интеллекта, созданная для студентов-психологов и недавних выпускников, начинающих клиническую практику. Пользователи проводят текстовые терапевтические сессии; по завершении каждой сессии ИИ-супервизор разбирает разговор и составляет развивающий отчёт обратной связи. ИИ-пациент переносит терапевтические отношения от сессии к сессии благодаря сквозной памяти.",
        ],
      },
      {
        heading: "Наша миссия",
        paras: [
          "Наша цель — дать будущим специалистам в области психического здоровья безопасную и повторяемую среду, где можно отрабатывать навыки клинической беседы без какого-либо риска навредить реальным клиентам. PsyPractice не является образовательной или преподавательской услугой; это программный инструмент, который позволяет пользователю вести и фиксировать собственную практику. Это не реальная терапия или консультирование, это не замена клинической супервизии, это не диагностический или оценочный инструмент, и это нельзя использовать в кризисных или неотложных ситуациях.",
        ],
      },
      {
        heading: "Что мы предлагаем",
        bullets: [
          "Симуляция текстовых терапевтических сессий с разнообразными профилями пациентов, синтезированными из клинической литературы.",
          "Автоматические отчёты разбора супервизора после каждой сессии.",
          "Модель «живого пациента» со сквозной памятью, благодаря которой терапевтические отношения развиваются во времени.",
          "Создание собственных пациентов: студент может сгенерировать свой ИИ-случай.",
          "Метрики прогресса, отслеживающие развитие микронавыков.",
        ],
      },
      {
        heading: "Сведения о продавце",
        bullets: [
          `Наименование: ${MERCHANT.legalNameEn} (${MERCHANT.typeRu})`,
          `Адрес: ${MERCHANT.addressEn}`,
          `Налоговая инспекция / ИНН: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}`,
          `Электронная почта: ${MERCHANT.email}`,
          `Сайт: ${MERCHANT.website}`,
        ],
      },
    ],
  },
  {
    id: "distance-sales",
    title: "Договор дистанционной продажи",
    description:
      "Продавец, покупатель, услуга, цена, оплата, исполнение и условия права на отказ согласно Закону Турции № 6502 и Положению о дистанционных договорах.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "1. Стороны",
        bullets: [
          `ПРОДАВЕЦ: ${MERCHANT.legalNameEn} (${MERCHANT.typeRu}). Адрес: ${MERCHANT.addressEn}. Налоговая инспекция / ИНН: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}. Электронная почта: ${MERCHANT.email}.`,
          "ПОКУПАТЕЛЬ: физическое или юридическое лицо, создающее учётную запись на Платформе и приобретающее платную подписку (далее — «Покупатель»).",
        ],
      },
      {
        heading: "2. Предмет договора",
        paras: [
          "Предметом настоящего Договора является определение прав и обязанностей сторон в соответствии с Законом Турции № 6502 о защите прав потребителей и Положением о дистанционных договорах в отношении продажи и исполнения подписки на цифровую услугу, приобретённой Покупателем на Платформе в электронной форме, характеристики и цена которой указаны ниже.",
        ],
      },
      {
        heading: "3. Характер услуги и цена",
        bullets: [
          "Продаваемая услуга — это функции симуляции пациента на базе ИИ, предоставляемые в рамках платного тарифного плана программного обеспечения PsyPractice (цифровая услуга / право пользования программой).",
          "PsyPractice не является образовательной или преподавательской услугой; это программный инструмент, который позволяет пользователю вести и фиксировать собственную практику. Плата за подписку — это встречное предоставление за доступ к этой программе; это не плата за занятия, учебную программу, экзамены, консультации или какой-либо документ.",
          "Состав тарифа, срок (месячный или годовой) и актуальная цена ясно отображаются на экране тарифов или оформления заказа Платформы в момент покупки; общая сумма с учётом налогов раскрывается Покупателю до оплаты.",
          "Цены действуют в валюте, отображаемой на Платформе; если подписка не отменена, она автоматически продлевается по той же цене по окончании выбранного срока.",
        ],
      },
      {
        heading: "4. Оплата",
        paras: [
          "Платежи по кредитным и дебетовым картам принимаются в защищённом режиме через лицензированную платёжную организацию iyzico. Данные карты Продавцом не хранятся; платёж обрабатывается в защищённых системах iyzico. При повторяющихся (подписочных) платежах сумма автоматически списывается в каждом периоде продления с карты, одобренной Покупателем.",
        ],
      },
      {
        heading: "5. Исполнение и предоставление",
        paras: [
          "Услуга является цифровой и не предполагает физической доставки. После подтверждения платежа функции подписки становятся доступны в учётной записи Покупателя незамедлительно. Исполнение осуществляется в электронной форме путём предоставления бесперебойного доступа в течение всего срока подписки.",
        ],
      },
      {
        heading: "6. Право на отказ и исключение из него",
        bullets: [
          "Согласно статье 15/1-(ğ) Положения о дистанционных договорах право на отказ не может быть реализовано в отношении нематериальных (цифровых) товаров и услуг, исполняемых мгновенно в электронной форме, если исполнение начато с согласия Покупателя.",
          "Покупатель в момент покупки признаёт, что услуга будет исполнена немедленно и что, следовательно, право на отказ отсутствует. Покупатель может в любой момент отменить подписку, чтобы прекратить продление; отмена вступает в силу по окончании текущего оплаченного периода.",
          "Подробные условия возврата см. в разделе «Условия предоставления услуги и возврата».",
        ],
      },
      {
        heading: "7. Споры и вступление в силу",
        paras: [
          "В случае спора Покупатель может обратиться в Потребительскую арбитражную комиссию или в потребительские суды по месту своего жительства в пределах денежных лимитов, объявленных Министерством торговли. Настоящий Договор считается заключённым и вступает в силу с момента его одобрения Покупателем в электронной форме.",
        ],
      },
    ],
  },
  {
    id: "delivery-refund",
    title: "Условия предоставления услуги и возврата",
    description:
      "Предоставление цифровой услуги, отмена подписки, исключение из права на отказ и условия возврата средств.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "Предоставление (цифровая услуга)",
        bullets: [
          "PsyPractice — полностью цифровая услуга; физической отправки товара или курьерской доставки не существует.",
          "В момент подтверждения платежа функции подписки активируются в учётной записи Покупателя автоматически и мгновенно; отдельного срока предоставления или платы за доставку нет.",
          "Доступ к услуге осуществляется через веб-браузер любого устройства с подключением к интернету.",
        ],
      },
      {
        heading: "Отмена подписки",
        bullets: [
          "Покупатель может отменить подписку в любой момент в настройках учётной записи. Отмена вступает в силу по окончании текущего оплаченного периода и прекращает следующее автоматическое продление.",
          "После отмены доступ к услуге сохраняется до конца оплаченного периода.",
        ],
      },
      {
        heading: "Право на отказ и возврат средств",
        bullets: [
          "Поскольку услуга является цифровой и исполняется немедленно с согласия Покупателя, по общему правилу право на отказ — а значит, и право на возврат — согласно статье 15/1-(ğ) Положения о дистанционных договорах не возникает.",
          "В случаях явного недостатка — например, ошибочного или двойного списания, полной невозможности получить доступ к услуге по техническим причинам и тому подобного — Покупатель может запросить возврат средств, обратившись к Продавцу в разумный срок с даты платежа.",
          `Запросы на возврат направляются на ${MERCHANT.email}; одобренные возвраты перечисляются на карту, использованную для оплаты, через инфраструктуру iyzico, как правило, в течение 14 дней. Срок возврата может отличаться в зависимости от времени обработки банком.`,
        ],
      },
      {
        heading: "Контакты",
        paras: [
          `По всем вопросам предоставления услуги, отмены и возврата: ${MERCHANT.email}${MERCHANT.phone ? " · " + MERCHANT.phone : ""}.`,
        ],
      },
    ],
  },
];

const DOCS_BY_LOCALE: Record<Locale, LegalDoc[]> = {
  en: DOCS_EN,
  tr: DOCS_TR,
  ru: DOCS_RU,
};

export function getLegalDocs(locale: Locale): LegalDoc[] {
  return DOCS_BY_LOCALE[locale] ?? DOCS_EN;
}
